import { useState, useMemo, useEffect, useCallback, useRef, memo, type ReactNode } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { Panel, PanelGroup, PanelResizeHandle, type ImperativePanelHandle } from 'react-resizable-panels';
import { ChevronRight, ChevronDown, ChevronLeft, Folder, FolderOpen, User, Download, X, Play, Filter, PanelLeftClose, PanelLeft, Star, AlertTriangle, CheckSquare, Square, Info, Plus, FolderPlus, FolderMinus, Upload, Pencil, Check, Loader2 } from 'lucide-react';
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import * as TooltipPrimitive from '@radix-ui/react-tooltip@1.1.8';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { VideoPlayer } from './ui/video-player';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { SecondarySidebar, SecondarySidebarHeader, SecondarySidebarSearch } from './SecondarySidebar';
import { prepareImageForUpload } from '@/utils/prepareImageForUpload';
import { showError } from '@/utils/alert';
import {
  addContentHubCollectionMedias,
  archiveContentHubCollection,
  createContentHubCollection,
  getContentHubFavoritesSearch,
  getContentHubPaths,
  getContentHubSearch,
  likeContentHubMedia,
  removeContentHubCollectionMedias,
  unlikeContentHubMedia,
  updateContentHubCollection,
  uploadContentHubCollectionPicture,
  type ContentHubCollection,
  type ContentHubCollectionVisibility,
  type ContentHubKind,
  type ContentHubMedia,
  type ContentHubMediaPage,
  type ContentHubPathRow,
} from '@/api/beatlyApi';
import i18n from '@/i18n';
import { getPathForPage } from '@/routes';
import { getTokenFromStorage, logout } from '@/utils/auth';
import {
  CONTENT_HUB_PAGE_SIZE,
  CONTENT_HUB_QUERY_PERSISTENCE,
  fetchAllCollections,
} from '@/utils/contentHubPrefetch';
import { formatContentHubDisplayFilename } from '@/utils/contentHubDisplayFilename';
import { THEME } from '@/components/campaignResults/theme';

const COLLECTION_CARD_GRADIENTS = [
  'from-purple-600 to-blue-600',
  'from-pink-600 to-rose-600',
  'from-green-600 to-emerald-600',
  'from-orange-600 to-amber-600',
] as const;

// Per-folder/view scroll positions for the right-side gallery container. Lives at
// module scope so it survives a ContentHubPage unmount/remount during the session
// (e.g. user navigates away to /campaigns and back to /content-hub).
const galleryScrollByKey = new Map<string, number>();

interface LicenseRight {
  type: 'republishing' | 'sponsorship' | 'editing';
  label: string;
  expiry: string | null; // null = indefinite
}

const CONTENT_HUB_NOT_AVAILABLE = i18n.t('contentHub.notAvailable');
const CONTENT_HUB_LOADING = i18n.t('contentHub.loadingContent');
const CONTENT_HUB_SECTION_TITLE_CONTENT = i18n.t('contentHub.sectionTitle.content');
const CONTENT_HUB_SECTION_TITLE_FAVORITES = i18n.t('contentHub.sectionTitle.favoriteContent');
const CONTENT_HUB_COLLECTION_VISIBILITY_LABEL = i18n.t('contentHub.collections.visibility.label');
const CONTENT_HUB_COLLECTION_VISIBILITY_PRIVATE = i18n.t('contentHub.collections.visibility.options.private');
const CONTENT_HUB_COLLECTION_VISIBILITY_TEAM = i18n.t('contentHub.collections.visibility.options.team');

function normalizeCollectionVisibility(v: unknown): ContentHubCollectionVisibility {
  const raw = typeof v === 'string' ? v.trim().toUpperCase() : '';
  if (raw === 'TEAM' || raw === 'TEAMS') return 'TEAM';
  return 'PRIVATE';
}

function getCollectionCardPreviewUrlFromMedia(m: ContentHubMedia): string | null {
  const preview = typeof m.preview_url === 'string' ? m.preview_url.trim() : '';
  if (preview) return preview;

  const url = typeof m.contenthub_url === 'string' ? m.contenthub_url.trim() : '';
  if (!url) return null;

  const lower = url.toLowerCase();
  const isImage = /\.(png|jpe?g|gif|webp)(\?|#|$)/.test(lower);
  if (isImage) return url;

  const filename = formatContentHubDisplayFilename(m);
  if (isVideoFilename(filename) || isVideoUrl(url)) {
    // Cloudinary serves auto-generated poster JPGs; for everything else the
    // derived `.jpg` does not exist on the CDN, so fall back to the raw video
    // URL — the card renders a <video> element and the browser uses the
    // first frame as its poster.
    const cloudinaryPoster = isCloudinaryVideoUrl(url) ? getContentHubVideoPreviewUrl(url) : undefined;
    return cloudinaryPoster ?? url;
  }

  return null;
}

function normalizeHandle(h: string | null | undefined) {
  return (h ?? '').trim().replace(/^@/, '').toLowerCase();
}

function normalizeName(n: string | null | undefined) {
  return (n ?? '').trim().toLowerCase();
}

/** Four-digit years 1900–2099 in the query string. */
function extractYearsFromQuery(q: string): number[] {
  const years = new Set<number>();
  const re = /\b(19|20)\d{2}\b/g;
  let m: RegExpExecArray | null;
  const s = q.trim();
  while ((m = re.exec(s)) !== null) {
    years.add(parseInt(m[0], 10));
  }
  return [...years];
}

function textQueryWithoutYears(q: string): string {
  return q
    .trim()
    .replace(/\b(19|20)\d{2}\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizePathRowCountryCodes(row: ContentHubPathRow): string[] {
  const raw = row.campaign_countries;
  if (!Array.isArray(raw)) return [];
  const upper = raw.map((c) => String(c).trim().toUpperCase()).filter(Boolean);
  return [...new Set(upper)].sort();
}

function formatCountryCodeLabel(code: string, locale: string): string {
  try {
    const dn = new Intl.DisplayNames([locale, 'en'], { type: 'region' });
    const name = dn.of(code);
    return name && name !== code ? name : code;
  } catch {
    return code;
  }
}

interface ContentItem {
  id: number;
  type: 'image' | 'video';
  thumbnail: string;
  mediaUrl: string;
  posterUrl?: string;
  mediaId: string;
  title: string;
  creator: string;
  creatorCountry: string;
  campaign: string;
  uploadDate: string;
  rights: LicenseRight[];
  ctr: string;
  engagementRate: string;
  views: string;
  downloads: number;
  format: string;
  size: string;
  duration?: string;
  isRaw?: boolean;
}

function getFileExtension(filename: string | undefined) {
  if (!filename) return '';
  const idx = filename.lastIndexOf('.');
  if (idx === -1) return '';
  return filename.slice(idx + 1).toLowerCase();
}

function isVideoFilename(filename: string | undefined) {
  const ext = getFileExtension(filename);
  return ['mp4', 'mov', 'webm', 'ogg', 'avi', 'm4v'].includes(ext);
}

function isVideoUrl(url: string): boolean {
  if (!url) return false;
  const [baseUrl] = url.split('?');
  return /\.(mp4|mov|avi|webm|ogg|m4v)$/i.test(baseUrl);
}

function getVideoPosterUrl(url: string): string | undefined {
  if (!url) return undefined;
  const [baseUrl, query] = url.split('?');
  const isVideo = /\.(mp4|mov|avi|webm|ogg|m4v)$/i.test(baseUrl);
  if (!isVideo) return undefined;
  const posterBaseUrl = baseUrl.replace(/\.(mp4|mov|avi|webm|ogg|m4v)$/i, '.jpg');
  return query ? `${posterBaseUrl}?${query}` : posterBaseUrl;
}

function isCloudinaryVideoUrl(url: string): boolean {
  if (!url) return false;
  const [baseUrl] = url.split('?');
  return baseUrl.includes('res.cloudinary.com') && baseUrl.includes('/video/upload/');
}

function getContentHubVideoPreviewUrl(url: string): string | undefined {
  if (!url) return undefined;
  const [baseUrl] = url.split('?');
  if (!isCloudinaryVideoUrl(url)) return getVideoPosterUrl(url);
  const withFrame = baseUrl.replace('/video/upload/', '/video/upload/so_0/');
  return withFrame.replace(/\.(mp4|mov|avi|webm|ogg|m4v)$/i, '.jpg');
}

function getUrlHost(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

function getCloudinaryAttachmentUrl(url: string, filename: string): string | null {
  try {
    const u = new URL(url);
    if (!u.hostname.includes('cloudinary.com')) return null;

    // Cloudinary supports forcing downloads via the `fl_attachment` flag.
    // We insert it right after `/upload/` so existing transforms keep working.
    //
    // Examples:
    // - /image/upload/v123/a.jpg            -> /image/upload/fl_attachment/v123/a.jpg
    // - /video/upload/q_auto,f_auto/v1/a.mp4 -> /video/upload/fl_attachment/q_auto,f_auto/v1/a.mp4
    const replaced = u.pathname.replace(/\/(image|video|raw)\/upload\//, '/$1/upload/fl_attachment/');
    if (replaced === u.pathname) return null;
    u.pathname = replaced;

    // Best-effort: suggest a filename to Cloudinary (not always honored, but safe).
    // We pass it as a query param used by some CDNs/proxies; Cloudinary ignores unknown params.
    if (filename) {
      u.searchParams.set('filename', filename);
    }
    return u.toString();
  } catch {
    return null;
  }
}

function triggerBrowserDownload(url: string) {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);
  window.setTimeout(() => iframe.remove(), 10_000);
}


function formatUploadDate(raw: string | undefined): string {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function mapContentHubMediaToContentItem(media: ContentHubMedia, idx: number): ContentItem {
  const url = typeof media.contenthub_url === 'string' ? media.contenthub_url : '';
  const previewUrl = typeof media.preview_url === 'string' ? media.preview_url : '';
  const filename = formatContentHubDisplayFilename(media);
  const isVideo = isVideoFilename(filename) || isVideoUrl(url);
  const posterUrl = isVideo ? (previewUrl || getContentHubVideoPreviewUrl(url)) : undefined;
  // Prefer the lighter preview_url for image thumbnails; fall back to full asset URL.
  const thumb = isVideo ? (posterUrl ?? '') : (previewUrl || url);
  const mediaId = typeof media.media_id === 'string' ? media.media_id : '';

  return {
    id: idx + 1,
    type: isVideo ? 'video' : 'image',
    thumbnail: thumb,
    mediaUrl: url,
    posterUrl,
    mediaId,
    title: filename || CONTENT_HUB_NOT_AVAILABLE,
    creator:
      (typeof media.influencer_name === 'string' && media.influencer_name) ||
      (typeof media.influencer_handle === 'string' && media.influencer_handle) ||
      CONTENT_HUB_NOT_AVAILABLE,
    creatorCountry: CONTENT_HUB_NOT_AVAILABLE,
    campaign: CONTENT_HUB_NOT_AVAILABLE,
    uploadDate: formatUploadDate(typeof media.date_created === 'string' ? media.date_created : undefined),
    rights: [],
    ctr: CONTENT_HUB_NOT_AVAILABLE,
    engagementRate: CONTENT_HUB_NOT_AVAILABLE,
    views: CONTENT_HUB_NOT_AVAILABLE,
    downloads: 0,
    format: getFileExtension(filename).toUpperCase() || CONTENT_HUB_NOT_AVAILABLE,
    size: CONTENT_HUB_NOT_AVAILABLE,
    duration: undefined,
    isRaw: typeof media.is_raw === 'boolean' ? media.is_raw : undefined,
  };
}

interface TreeNode {
  id: string;
  label: string;
  type: 'campaign' | 'creator' | 'collection';
  avatarUrl?: string;
  children?: TreeNode[];
  expanded?: boolean;
  /** Campaign start year (from API); only on campaign folder rows under Campaigns. */
  campaignYear?: number | null;
  /** ISO alpha-2 codes for sidebar country filter; campaign folder rows only. */
  campaignCountryCodes?: string[];
}

function getDaysUntilExpiry(expiryDate: string): number {
  const expiry = new Date(expiryDate);
  const today = new Date();
  const diffTime = expiry.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function getEarliestExpiry(item: ContentItem): string | null {
  const dated = item.rights.filter((r) => r.expiry !== null).map((r) => r.expiry as string);
  if (dated.length === 0) return null;
  return dated.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
}

function isItemExpiringSoon(item: ContentItem): boolean {
  const earliest = getEarliestExpiry(item);
  if (!earliest) return false;
  const daysUntil = getDaysUntilExpiry(earliest);
  return daysUntil >= 0 && daysUntil <= 30;
}

function isItemExpired(item: ContentItem): boolean {
  const earliest = getEarliestExpiry(item);
  if (!earliest) return false;
  return getDaysUntilExpiry(earliest) < 0;
}

interface ContentTileProps {
  item: ContentItem;
  isSelected: boolean;
  isFavorite: boolean;
  isHovered: boolean;
  onToggleSelection: (id: number, event: React.MouseEvent) => void;
  onToggleFavorite: (item: ContentItem, event: React.MouseEvent) => void;
  onOpenPreview: (item: ContentItem) => void;
  onMouseEnter: (id: number) => void;
  onMouseLeave: () => void;
}

interface FilenameTooltipProps {
  filename: string;
  children: ReactNode;
}

function FilenameTooltip({ filename, children }: FilenameTooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={150}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <div className="block min-w-0 cursor-help rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/40" tabIndex={0}>
            {children}
          </div>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="bottom"
            align="start"
            sideOffset={6}
            className="z-50 max-w-xs break-words rounded-md border bg-white p-3 text-left text-[12px] leading-snug text-[#101828] shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
            style={{ borderColor: THEME.border }}
          >
            {filename}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

const ContentTile = memo(function ContentTile({
  item,
  isSelected,
  isFavorite,
  isHovered,
  onToggleSelection,
  onToggleFavorite,
  onOpenPreview,
  onMouseEnter,
  onMouseLeave,
}: ContentTileProps) {
  const expiringSoon = isItemExpiringSoon(item);
  const expired = isItemExpired(item);

  return (
    <div
      className="relative group"
      style={{ width: 200 }}
      onMouseEnter={() => onMouseEnter(item.id)}
      onMouseLeave={onMouseLeave}
    >
      <Card className={`overflow-hidden cursor-pointer transition-all duration-200 w-full ${
        isSelected ? 'ring-4 ring-purple-500 border-purple-500' : 'border-gray-200 hover:shadow-lg'
      }`}>
        <div className="relative w-full bg-gray-100 flex items-center justify-center" style={{ height: 300 }}>
          <button
            onClick={(e) => onToggleSelection(item.id, e)}
            className={`absolute top-2 left-2 z-10 w-5 h-5 rounded-sm flex items-center justify-center transition-all ${
              isSelected
                ? 'bg-[#be43ff] text-white'
                : 'bg-black/30 text-white/80 opacity-0 group-hover:opacity-100 backdrop-blur-sm'
            }`}
          >
            {isSelected ? (
              <CheckSquare className="w-3.5 h-3.5" />
            ) : (
              <Square className="w-3.5 h-3.5" />
            )}
          </button>

          <button
            onClick={(e) => onToggleFavorite(item, e)}
            className={`absolute top-2 right-2 z-10 transition-all ${
              isFavorite
                ? 'text-[#a9f600] drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]'
                : 'text-white/70 opacity-0 group-hover:opacity-100 drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)] hover:text-[#a9f600]'
            }`}
          >
            <Star className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>

          {(expiringSoon || expired) && (
            <div className={`absolute top-2 left-8 z-10 ${
              expired ? 'text-[#ff5635]/70' : 'text-amber-500/70'
            }`} title={expired ? 'Expired' : `Expires in ${getDaysUntilExpiry(getEarliestExpiry(item)!)} days`}>
              <AlertTriangle className="w-3.5 h-3.5 drop-shadow-sm" />
            </div>
          )}

          {item.type === 'video' ? (
            item.posterUrl || item.thumbnail ? (
              <img
                src={item.posterUrl || item.thumbnail}
                alt={item.title}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-center"
                onClick={() => onOpenPreview(item)}
              />
            ) : (
              <div
                className="w-full h-full bg-gray-200"
                onClick={() => onOpenPreview(item)}
              />
            )
          ) : (
            <img
              src={item.thumbnail}
              alt={item.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover object-center"
              onClick={() => onOpenPreview(item)}
            />
          )}
          {item.type === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center" onClick={() => onOpenPreview(item)}>
              <div className="w-8 h-8 rounded-full bg-[#2e024c]/60 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <Play className="w-3.5 h-3.5 text-white ml-0.5" />
              </div>
            </div>
          )}
          {item.type === 'video' && item.duration && (
            <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm text-white rounded-sm" style={{ fontSize: '10px' }}>
              {item.duration}
            </div>
          )}
          {item.isRaw !== undefined && (
            <div
              className={`absolute bottom-1.5 left-1.5 z-10 px-1.5 py-0.5 rounded-sm font-medium backdrop-blur-sm border ${
                item.isRaw
                  ? 'bg-amber-100/90 text-amber-800 border-amber-300'
                  : 'bg-emerald-100/90 text-emerald-800 border-emerald-300'
              }`}
              style={{ fontSize: '10px' }}
            >
              {item.isRaw ? i18n.t('contentHub.tags.raw') : i18n.t('contentHub.tags.final')}
            </div>
          )}
          {isHovered && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" onClick={() => onOpenPreview(item)} />
          )}
        </div>
      </Card>
      <div className="mt-1.5 px-0.5">
        <FilenameTooltip filename={item.title}>
          <p className="text-gray-800 text-xs line-clamp-1">{item.title}</p>
        </FilenameTooltip>
      </div>
    </div>
  );
});

interface CollectionCardProps {
  collection: ContentHubCollection;
  gradient: string;
  isSelected: boolean;
  previewUrl: string | null;
  itemsCount: number | null;
  onSelect: () => void;
  onEdit: () => void;
  onPreviewLoaded: (collectionId: string, url: string) => void;
}

const CollectionCard = memo(function CollectionCard({
  collection,
  gradient,
  isSelected,
  previewUrl,
  itemsCount,
  onSelect,
  onEdit,
  onPreviewLoaded,
}: CollectionCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const itemsCountValue =
    typeof collection.content_count === 'number'
      ? collection.content_count
      : typeof collection.media_count === 'number'
        ? collection.media_count
        : typeof collection.item_count === 'number'
          ? collection.item_count
          : 0;

  const needsPreview = !collection.picture_url && !previewUrl && itemsCountValue > 0;

  // Per-card lazy preview hydration; replaces the eager N+1 fetch in loadCollections.
  const previewQuery = useQuery({
    queryKey: ['contenthub', 'collectionPreview', collection.collection_id],
    queryFn: async () => {
      const token = getTokenFromStorage();
      if (!token) return null;
      const page = await getContentHubSearch(token, {
        collectionId: collection.collection_id,
        pageNumber: 0,
        pageSize: 1,
        text: null,
      });
      const m = Array.isArray(page?.medias) ? page.medias[0] : null;
      return m ? getCollectionCardPreviewUrlFromMedia(m) : null;
    },
    enabled: inView && needsPreview,
    retry: 0,
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });

  useEffect(() => {
    const url = previewQuery.data;
    if (url) {
      onPreviewLoaded(collection.collection_id, url);
    }
  }, [collection.collection_id, onPreviewLoaded, previewQuery.data]);

  return (
    <div ref={cardRef}>
      <Card
        className={`group relative cursor-pointer overflow-hidden border bg-white transition-all duration-200 rounded-2xl ${
          isSelected
            ? 'ring-4 ring-purple-500 border-purple-500'
            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }`}
        style={{ height: '88px' }}
        onClick={onSelect}
      >
        <div className="relative w-full h-full flex">
          <div className="relative w-[88px] flex-shrink-0">
            <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-80`} />
            {(() => {
              if (!previewUrl) return null;
              // Non-Cloudinary CDNs do not auto-generate JPG posters, so the
              // safe way to show a thumbnail for a video is a muted <video>
              // element — the browser renders the seeked frame as its poster.
              // The `#t=0.001` Media Fragments hint forces the browser to seek
              // to (and lock) the first frame as the visible content; without
              // it, some browsers show the frame momentarily and then revert
              // to a blank/error state when metadata loading completes.
              if (isVideoUrl(previewUrl)) {
                const videoSrc = previewUrl.includes('#') ? previewUrl : `${previewUrl}#t=0.001`;
                return (
                  <video
                    key={videoSrc}
                    src={videoSrc}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover mix-blend-overlay"
                  />
                );
              }
              return (
                <img
                  src={previewUrl}
                  alt={collection.name}
                  className="w-full h-full object-cover mix-blend-overlay"
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                />
              );
            })()}
          </div>
          <div className="flex-1 px-4 py-3 flex flex-col justify-center min-w-0">
            <h3 className="text-[#101828] mb-0.5 line-clamp-1 text-[15px]" style={{ fontWeight: 700 }}>
              {collection.name}
            </h3>
            <p className="text-[#667085] text-[13px]">
              {itemsCount === null ? CONTENT_HUB_NOT_AVAILABLE : itemsCount} items
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="absolute right-3 top-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/90 hover:bg-white text-gray-600 shadow-sm border border-gray-100"
            aria-label="Edit collection"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
      </Card>
    </div>
  );
});

export type ContentHubPageProps = {
  previewMediaId?: string;
  navigate: NavigateFunction;
};

export function ContentHubPage({ previewMediaId, navigate }: ContentHubPageProps) {

  const ACCEPTED_COLLECTION_PICTURE_TYPES = useMemo(
    () => ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const,
    []
  );
  const MAX_COLLECTION_PICTURE_BYTES = 10 * 1024 * 1024;

  const validateCollectionPictureFile = useCallback(
    (file: File): string | null => {
      const isAcceptedType = (ACCEPTED_COLLECTION_PICTURE_TYPES as readonly string[]).includes(file.type);
      if (!isAcceptedType) {
        return 'Please select a valid image file (JPG, PNG, GIF, or WebP)';
      }
      if (file.size > MAX_COLLECTION_PICTURE_BYTES) {
        return 'File is too large. Maximum size is 10MB.';
      }
      return null;
    },
    [ACCEPTED_COLLECTION_PICTURE_TYPES]
  );

  const queryClient = useQueryClient();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedTreeNodeId, setSelectedTreeNodeId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [isPreviewVideoPlaying, setIsPreviewVideoPlaying] = useState(false);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const [hoveredContent, setHoveredContent] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [campaignFolderSort, setCampaignFolderSort] = useState<'year' | 'name'>('year');
  const [selectedCampaignCountryCode, setSelectedCampaignCountryCode] = useState<string | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const browsePanelRef = useRef<ImperativePanelHandle>(null);
  const [isCreateCollectionOpen, setIsCreateCollectionOpen] = useState(false);
  const [isAddToCollectionOpen, setIsAddToCollectionOpen] = useState(false);
  const [isEditCollectionOpen, setIsEditCollectionOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [newCollectionVisibility, setNewCollectionVisibility] = useState<ContentHubCollectionVisibility>('PRIVATE');
  const [newPictureFile, setNewPictureFile] = useState<File | null>(null);
  const [selectedAddToCollectionIds, setSelectedAddToCollectionIds] = useState<string[]>([]);
  const [preserveAddToCollectionSelectionOnClose, setPreserveAddToCollectionSelectionOnClose] = useState(false);
  const [resumeAddToCollectionAfterCreate, setResumeAddToCollectionAfterCreate] = useState(false);
  const [pendingSelectAddToCollectionId, setPendingSelectAddToCollectionId] = useState<string | null>(null);
  const [collectionActionMediaIds, setCollectionActionMediaIds] = useState<string[]>([]);
  const [collectionActionSource, setCollectionActionSource] = useState<'bulk' | 'single'>('bulk');
  const [collectionPreviewById, setCollectionPreviewById] = useState<Record<string, string>>({});
  const [editingCollectionId, setEditingCollectionId] = useState<string | null>(null);
  const [editCollectionName, setEditCollectionName] = useState('');
  const [editCollectionDescription, setEditCollectionDescription] = useState('');
  const [editCollectionVisibility, setEditCollectionVisibility] = useState<ContentHubCollectionVisibility>('PRIVATE');
  const [editCollectionOriginalVisibility, setEditCollectionOriginalVisibility] = useState<ContentHubCollectionVisibility>('PRIVATE');
  const [editPictureFile, setEditPictureFile] = useState<File | null>(null);
  const [isSavingCollection, setIsSavingCollection] = useState(false);
  const [isArchivingCollection, setIsArchivingCollection] = useState(false);
  
  // Feature 1: Multi-select
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  
  // Feature 2: Active filtering
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    type: 'all' as 'all' | 'image' | 'video',
    campaign: 'all',
    creator: 'all',
    expiringDays: 0,
    showFavoritesOnly: false,
    kind: 'all' as ContentHubKind,
  });
  
  // Feature 3: Favorites
  const [favorites, setFavorites] = useState<string[]>([]);
  // Local override applied after optimistic favorite toggles or once we know the gallery total.
  const [favoritesCountOverride, setFavoritesCountOverride] = useState<number | null>(null);

  /** Media rows resolved only via deep-link search (not in current `visibleMedias`). */
  const [deepLinkMediaById, setDeepLinkMediaById] = useState<Record<string, ContentHubMedia>>({});

  const openContentPreview = useCallback(
    (item: ContentItem) => {
      if (!item.mediaId) return;
      setSelectedContent(item);
      navigate(getPathForPage('content-hub', { contentHubMediaId: item.mediaId }), { replace: false });
    },
    [navigate]
  );

  const closeContentPreview = useCallback(() => {
    setSelectedContent(null);
    setDeepLinkMediaById({});
    navigate(getPathForPage('content-hub'), { replace: true });
  }, [navigate]);

  const openCreateCollectionDialog = useCallback(() => {
    // Create dialog must ALWAYS default to Private on open.
    setNewCollectionVisibility('PRIVATE');
    setIsCreateCollectionOpen(true);
  }, []);
  
  // Audio Dubbing Feature
  const [_isDubbingDialogOpen, _setIsDubbingDialogOpen] = useState(false);
  const [_isBulkDubbingOpen, _setIsBulkDubbingOpen] = useState(false);
  const [_dubbingMethod, _setDubbingMethod] = useState<'ai' | 'upload'>('ai');
  const [_selectedLanguages, _setSelectedLanguages] = useState<string[]>([]);
  const [_uploadedAudioFile, _setUploadedAudioFile] = useState<File | null>(null);
  const [_activeAudioLanguage, _setActiveAudioLanguage] = useState('en');
  
  // Mock audio tracks data (in real app, this would be per-video)
  const [_audioTracks] = useState<Record<number, Array<{
    language: string;
    languageName: string;
    flag: string;
    status: 'ready' | 'processing' | 'failed';
    createdDate: string;
    createdBy: 'AI' | 'Upload';
    duration: string;
  }>>>({
    2: [
      { language: 'en', languageName: 'English', flag: '🇬🇧', status: 'ready', createdDate: 'Nov 10, 2024', createdBy: 'Upload', duration: '0:15' },
      { language: 'es', languageName: 'Spanish', flag: '🇪🇸', status: 'ready', createdDate: 'Nov 12, 2024', createdBy: 'AI', duration: '0:15' },
      { language: 'fr', languageName: 'French', flag: '🇫🇷', status: 'ready', createdDate: 'Nov 12, 2024', createdBy: 'AI', duration: '0:15' },
    ],
    4: [
      { language: 'en', languageName: 'English', flag: '🇬🇧', status: 'ready', createdDate: 'Nov 5, 2024', createdBy: 'Upload', duration: '0:12' },
      { language: 'de', languageName: 'German', flag: '🇩🇪', status: 'ready', createdDate: 'Nov 8, 2024', createdBy: 'AI', duration: '0:12' },
      { language: 'pt', languageName: 'Portuguese', flag: '🇵🇹', status: 'processing', createdDate: 'Nov 15, 2024', createdBy: 'AI', duration: '0:12' },
    ],
    7: [
      { language: 'en', languageName: 'English', flag: '🇬🇧', status: 'ready', createdDate: 'Oct 25, 2024', createdBy: 'Upload', duration: '0:20' },
    ],
    9: [
      { language: 'en', languageName: 'English', flag: '🇬🇧', status: 'ready', createdDate: 'Oct 18, 2024', createdBy: 'Upload', duration: '0:18' },
      { language: 'ja', languageName: 'Japanese', flag: '🇯🇵', status: 'ready', createdDate: 'Oct 20, 2024', createdBy: 'AI', duration: '0:18' },
      { language: 'ko', languageName: 'Korean', flag: '🇰🇷', status: 'ready', createdDate: 'Oct 20, 2024', createdBy: 'AI', duration: '0:18' },
      { language: 'zh', languageName: 'Chinese', flag: '🇨🇳', status: 'ready', createdDate: 'Oct 20, 2024', createdBy: 'AI', duration: '0:18' },
    ],
    12: [
      { language: 'en', languageName: 'English', flag: '🇬🇧', status: 'ready', createdDate: 'Oct 8, 2024', createdBy: 'Upload', duration: '0:25' },
      { language: 'it', languageName: 'Italian', flag: '🇮🇹', status: 'failed', createdDate: 'Oct 10, 2024', createdBy: 'AI', duration: '0:25' },
    ],
  });
  
  const _availableLanguages = [
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  ];
  
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({
    campaigns: true,
    collections: true,
  });

  // Left side: paths tree. Independent useQuery so it never blocks the gallery.
  const pathsQuery = useQuery({
    queryKey: ['contenthub', 'paths'],
    queryFn: async () => {
      const token = getTokenFromStorage();
      if (!token) throw new Error('No auth token');
      const response = await getContentHubPaths(token);
      if (response?.isExpired) {
        logout();
        throw new Error('Token expired');
      }
      return response.folders ?? [];
    },
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });
  const contentHubPathRows: ContentHubPathRow[] = useMemo(
    () => pathsQuery.data ?? [],
    [pathsQuery.data],
  );
  const isLoadingContentHubPaths = pathsQuery.isPending;

  // Collections strip. Stable key: never depends on per-card preview state, breaks
  // the `loadCollections([collectionPreviewById])` cycle that re-fetched on every preview.
  const collectionsQuery = useQuery({
    queryKey: ['contenthub', 'collections'],
    queryFn: async () => {
      const token = getTokenFromStorage();
      if (!token) throw new Error('No auth token');
      // API defaults to scope=private. Team-visible collections require scope=team.
      const [priv, team] = await Promise.all([
        fetchAllCollections('private', token),
        fetchAllCollections('team', token),
      ]);
      const byId = new Map<string, ContentHubCollection>();
      [...priv, ...team].forEach((c) => {
        if (!c?.collection_id) return;
        byId.set(c.collection_id, c);
      });
      return Array.from(byId.values()).sort((a, b) =>
        String(b.date_updated ?? '').localeCompare(String(a.date_updated ?? '')),
      );
    },
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });
  const collections: ContentHubCollection[] = useMemo(
    () => collectionsQuery.data ?? [],
    [collectionsQuery.data],
  );
  const isLoadingCollections = collectionsQuery.isPending;

  const refreshCollections = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['contenthub', 'collections'] });
  }, [queryClient]);

  useEffect(() => {
    if (!pendingSelectAddToCollectionId) return;
    if (!collections.some((c) => c.collection_id === pendingSelectAddToCollectionId)) return;
    setSelectedAddToCollectionIds((prev) => {
      if (prev.includes(pendingSelectAddToCollectionId)) return prev;
      return [...prev, pendingSelectAddToCollectionId];
    });
    setPendingSelectAddToCollectionId(null);
    if (resumeAddToCollectionAfterCreate) {
      setIsAddToCollectionOpen(true);
      setResumeAddToCollectionAfterCreate(false);
    }
  }, [collections, pendingSelectAddToCollectionId, resumeAddToCollectionAfterCreate]);

  const editPicturePreviewUrl = useMemo(() => {
    if (!editPictureFile) return null;
    return URL.createObjectURL(editPictureFile);
  }, [editPictureFile]);

  const newPicturePreviewUrl = useMemo(() => {
    if (!newPictureFile) return null;
    return URL.createObjectURL(newPictureFile);
  }, [newPictureFile]);

  useEffect(() => {
    return () => {
      if (editPicturePreviewUrl) URL.revokeObjectURL(editPicturePreviewUrl);
      if (newPicturePreviewUrl) URL.revokeObjectURL(newPicturePreviewUrl);
    };
  }, [editPicturePreviewUrl, newPicturePreviewUrl]);

  const parseCountTotal = useCallback((raw: unknown): number | null => {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      const n = Number(raw);
      if (Number.isFinite(n)) return n;
    }
    return null;
  }, []);

  // Lightweight favorites count: a single page_size=1 fetch reading count_total.
  // Falls back to the gallery count when running in favorites mode (see effect below).
  const favoritesCountQuery = useQuery({
    queryKey: ['contenthub', 'favoritesCount'],
    queryFn: async (): Promise<number | null> => {
      const token = getTokenFromStorage();
      if (!token) return null;
      const page = await getContentHubFavoritesSearch(token, {
        pageNumber: 0,
        pageSize: 1,
        text: null,
      });
      if (page?.isExpired) {
        logout();
        return null;
      }
      return parseCountTotal((page as { count_total?: unknown } | null)?.count_total);
    },
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });
  const favoritesCountTotal: number | null =
    favoritesCountOverride ?? favoritesCountQuery.data ?? null;

  const defaultTeamFolderId = useMemo(() => contentHubPathRows[0]?.team_id ?? null, [contentHubPathRows]);

  useEffect(() => {
    // Always preselect the Campaigns root on first load.
    if (selectedTreeNodeId === null) setSelectedTreeNodeId('campaigns');
  }, [selectedTreeNodeId]);

  useEffect(() => {
    if (!defaultTeamFolderId) return;
    // On first load, default to Campaigns root + show all content (team folder).
    if (!selectedTreeNodeId) {
      setSelectedTreeNodeId('campaigns');
    }
    if (!selectedFolderId) {
      setSelectedFolderId(defaultTeamFolderId);
    }
  }, [defaultTeamFolderId, selectedFolderId, selectedTreeNodeId]);

  useEffect(() => {
    // When we have a team folder but no explicit UI selection yet,
    // default highlight to the Campaigns root (shows all content).
    if (!defaultTeamFolderId) return;
    if (selectedTreeNodeId !== null) return;
    setSelectedTreeNodeId('campaigns');
  }, [defaultTeamFolderId, selectedTreeNodeId]);

  const rowByCampaignId = useMemo(() => {
    const map = new Map<string, ContentHubPathRow>();
    contentHubPathRows.forEach((r) => map.set(r.campaign_id, r));
    return map;
  }, [contentHubPathRows]);

  const rowByCollaborationId = useMemo(() => {
    const map = new Map<string, ContentHubPathRow>();
    contentHubPathRows.forEach((r) => map.set(r.collaboration_id, r));
    return map;
  }, [contentHubPathRows]);

  const rowByTeamId = useMemo(() => {
    const map = new Map<string, ContentHubPathRow>();
    contentHubPathRows.forEach((r) => map.set(r.team_id, r));
    return map;
  }, [contentHubPathRows]);

  const collectionIdSet = useMemo(() => {
    return new Set(collections.map((c) => c.collection_id));
  }, [collections]);

  const folderType = useMemo(() => {
    if (!selectedFolderId) return 'team';
    if (collectionIdSet.has(selectedFolderId)) return 'collection';
    if (rowByCollaborationId.has(selectedFolderId)) return 'collaboration';
    if (rowByCampaignId.has(selectedFolderId)) return 'campaign';
    if (rowByTeamId.has(selectedFolderId)) return 'team';
    return 'team';
  }, [collectionIdSet, rowByCampaignId, rowByCollaborationId, rowByTeamId, selectedFolderId]);

  useEffect(() => {
    // Clear bulk selection when switching folder / favorites view.
    setSelectedItems([]);
  }, [activeFilters.showFavoritesOnly, folderType, selectedFolderId]);

  const selectedRow = useMemo(() => {
    if (!selectedFolderId) return null;
    if (folderType === 'collaboration') return rowByCollaborationId.get(selectedFolderId) ?? null;
    if (folderType === 'campaign') return rowByCampaignId.get(selectedFolderId) ?? null;
    if (folderType === 'team') return rowByTeamId.get(selectedFolderId) ?? null;
    return null;
  }, [folderType, rowByCampaignId, rowByCollaborationId, rowByTeamId, selectedFolderId]);

  const requestFolderId = useMemo(() => {
    return selectedFolderId ?? defaultTeamFolderId ?? null;
  }, [defaultTeamFolderId, selectedFolderId]);

  const fallbackFolderId = useMemo(() => {
    if (!requestFolderId) return null;
    if (requestFolderId === defaultTeamFolderId) return requestFolderId;
    if (collectionIdSet.has(requestFolderId)) return null;

    if (rowByCampaignId.has(requestFolderId)) return rowByCampaignId.get(requestFolderId)?.team_id ?? null;
    if (rowByCollaborationId.has(requestFolderId))
      return rowByCollaborationId.get(requestFolderId)?.team_id ?? null;

    return defaultTeamFolderId;
  }, [collectionIdSet, defaultTeamFolderId, requestFolderId, rowByCampaignId, rowByCollaborationId]);

  const filterBySelection = useMemo(() => {
    return (items: ContentHubMedia[]) => {
      // Favorites view should be global, independent of the current folder selection.
      if (activeFilters.showFavoritesOnly) return items;
      // Team root (Campaigns root) shows all team medias. The backend already scopes by team.
      if (!selectedFolderId || folderType === 'team') return items;
      if (folderType === 'collection') return items;
      if (folderType === 'campaign') return items.filter((m) => m.campaign_id === selectedFolderId);

      const campaignId = selectedRow?.campaign_id ?? null;
      const handle = normalizeHandle(selectedRow?.influencer_handle) || null;
      const name = normalizeName(selectedRow?.influencer_name) || null;

      return items.filter((m) => {
        if (m.collaboration_id === selectedFolderId) return true;
        if (!campaignId) return false;

        const mediaHandle = normalizeHandle(m.influencer_handle);
        const mediaName = normalizeName(m.influencer_name);
        const handleMatch = Boolean(handle) && mediaHandle === handle;
        const nameMatch = Boolean(name) && mediaName === name;

        return m.campaign_id === campaignId && (handleMatch || nameMatch);
      });
    };
  }, [activeFilters.showFavoritesOnly, folderType, selectedFolderId, selectedRow]);

  // Right side: gallery. Independent useInfiniteQuery, never blocks the paths tree.
  // Identity is the actual selection (folderType + selectionKey + favorites flag), so
  // sidebar/path loads do NOT invalidate the cached page and do NOT cause spinner flashes.
  const showFavoritesOnly = activeFilters.showFavoritesOnly;
  const gallerySelectionKey = folderType === 'team' ? '' : (selectedFolderId ?? '');
  // `fallbackFolderId` is intentionally NOT part of the key: it's a defensive
  // retry value read inside the queryFn closure when the backend doesn't
  // recognise the requested folder. Including it would invalidate the gallery
  // cache the moment the paths query resolves (defaultTeamFolderId goes from
  // null to a real id), causing a spurious refetch and a visible blink.
  // Favorites endpoint hardcodes "final only" server-side, so the kind filter
  // is ignored (and hidden in the UI) when showFavoritesOnly is true. Force the
  // query-key value to 'all' in that case so flipping favorites on/off doesn't
  // create stale variants of the cache.
  const effectiveKind: ContentHubKind = showFavoritesOnly ? 'all' : activeFilters.kind;
  const galleryQueryKey = useMemo(
    () => [
      'contenthub',
      'medias',
      showFavoritesOnly ? 'fav' : 'all',
      folderType,
      gallerySelectionKey,
      effectiveKind,
    ] as const,
    [showFavoritesOnly, folderType, gallerySelectionKey, effectiveKind],
  );
  // Stable string identity for the current folder/view; used as the scroll-position cache key.
  const galleryKeyStr = useMemo(() => JSON.stringify(galleryQueryKey), [galleryQueryKey]);
  const galleryScrollRef = useRef<HTMLDivElement | null>(null);

  // Save the current scroll position on every scroll event so the value in
  // galleryScrollByKey is always up-to-date by the time the user switches folders.
  const handleGalleryScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      galleryScrollByKey.set(galleryKeyStr, e.currentTarget.scrollTop);
    },
    [galleryKeyStr],
  );

  // Restore scroll position when the folder/view changes. requestAnimationFrame waits
  // one frame so the cached grid has laid out before we set scrollTop, otherwise the
  // browser would clamp the value against the not-yet-tall content.
  useEffect(() => {
    const el = galleryScrollRef.current;
    if (!el) return;
    const saved = galleryScrollByKey.get(galleryKeyStr) ?? 0;
    const id = requestAnimationFrame(() => {
      el.scrollTop = saved;
    });
    return () => cancelAnimationFrame(id);
  }, [galleryKeyStr]);

  const galleryQuery = useInfiniteQuery<ContentHubMediaPage, Error, InfiniteData<ContentHubMediaPage>, typeof galleryQueryKey, number>({
    queryKey: galleryQueryKey,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const token = getTokenFromStorage();
      if (!token) throw new Error('No auth token');

      // The favorites endpoint hardcodes "final only" server-side and does not
      // accept the `kind` query param, so we only forward `kind` to the regular
      // search endpoint (the showFavoritesOnly branch returns early below).
      const kindParam = effectiveKind;
      const fetchOnce = async (): Promise<ContentHubMediaPage> => {
        if (showFavoritesOnly) {
          return getContentHubFavoritesSearch(token, {
            pageNumber: pageParam,
            pageSize: CONTENT_HUB_PAGE_SIZE,
            text: null,
          });
        }
        if (folderType === 'campaign') {
          return getContentHubSearch(token, {
            campaignId: selectedFolderId,
            pageNumber: pageParam,
            pageSize: CONTENT_HUB_PAGE_SIZE,
            text: null,
            kind: kindParam,
          });
        }
        if (folderType === 'collaboration') {
          return getContentHubSearch(token, {
            collaborationId: selectedFolderId,
            pageNumber: pageParam,
            pageSize: CONTENT_HUB_PAGE_SIZE,
            text: null,
            kind: kindParam,
          });
        }
        if (folderType === 'collection') {
          return getContentHubSearch(token, {
            collectionId: selectedFolderId,
            pageNumber: pageParam,
            pageSize: CONTENT_HUB_PAGE_SIZE,
            text: null,
            kind: kindParam,
          });
        }
        return getContentHubSearch(token, {
          pageNumber: pageParam,
          pageSize: CONTENT_HUB_PAGE_SIZE,
          text: null,
          kind: kindParam,
        });
      };

      let page = await fetchOnce();
      if (page?.isExpired) {
        logout();
        throw new Error('Token expired');
      }

      // If backend doesn't recognize the requested folder, fall back to the team default.
      if (!Array.isArray(page?.medias) && fallbackFolderId && fallbackFolderId !== requestFolderId) {
        page = showFavoritesOnly
          ? await getContentHubFavoritesSearch(token, {
              pageNumber: pageParam,
              pageSize: CONTENT_HUB_PAGE_SIZE,
              text: null,
            })
          : await getContentHubSearch(token, {
              pageNumber: pageParam,
              pageSize: CONTENT_HUB_PAGE_SIZE,
              text: null,
              kind: kindParam,
            });
      }
      return page;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.has_more) return undefined;
      return allPages.length;
    },
    ...CONTENT_HUB_QUERY_PERSISTENCE,
  });

  const contentHubMedias = useMemo<ContentHubMedia[]>(() => {
    const pages = galleryQuery.data?.pages ?? [];
    const all: ContentHubMedia[] = [];
    pages.forEach((p) => {
      if (Array.isArray(p?.medias)) all.push(...p.medias);
    });
    return all;
  }, [galleryQuery.data]);

  const contentCountTotal = useMemo<number | null>(() => {
    const pages = galleryQuery.data?.pages ?? [];
    const last = pages[pages.length - 1];
    return parseCountTotal((last as { count_total?: unknown } | undefined)?.count_total);
  }, [galleryQuery.data, parseCountTotal]);

  const isLoadingContentHubMedias = galleryQuery.isPending;
  const isFetchingNextPage = galleryQuery.isFetchingNextPage;
  const hasNextPage = galleryQuery.hasNextPage;

  // Keep the favorites count badge in sync with the gallery total when in favorites mode.
  useEffect(() => {
    if (!showFavoritesOnly) return;
    if (typeof contentCountTotal === 'number') {
      setFavoritesCountOverride(contentCountTotal);
    } else if (!hasNextPage && contentHubMedias.length > 0) {
      setFavoritesCountOverride(contentHubMedias.length);
    }
  }, [showFavoritesOnly, contentCountTotal, hasNextPage, contentHubMedias.length]);

  // IntersectionObserver-driven infinite scroll. The sentinel sits below the grid;
  // when it scrolls into view (with rootMargin pre-fetch), we request the next page.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && galleryQuery.hasNextPage && !galleryQuery.isFetchingNextPage) {
          galleryQuery.fetchNextPage();
        }
      },
      { rootMargin: '600px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [galleryQuery]);

  const visibleMedias = useMemo(() => {
    return filterBySelection(contentHubMedias);
  }, [contentHubMedias, filterBySelection]);

  const campaignNameById = useMemo(() => {
    const map = new Map<string, string>();
    contentHubPathRows.forEach((r) => {
      if (!r.campaign_id) return;
      if (map.has(r.campaign_id)) return;
      map.set(r.campaign_id, r.campaign_name);
    });
    return map;
  }, [contentHubPathRows]);

  const availableCampaigns = useMemo(() => {
    const map = new Map<string, string>();
    contentHubPathRows.forEach((r) => {
      if (!r.campaign_id) return;
      if (map.has(r.campaign_id)) return;
      map.set(r.campaign_id, r.campaign_name);
    });

    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [contentHubPathRows]);

  const contentItems = useMemo<ContentItem[]>(() => {
    return visibleMedias.map((m, idx) => {
      const base = mapContentHubMediaToContentItem(m, idx);
      const campaignId = typeof m.campaign_id === 'string' ? m.campaign_id : '';
      const campaignName = campaignId ? campaignNameById.get(campaignId) : undefined;
      return {
        ...base,
        campaign: campaignName || base.campaign,
      };
    });
  }, [campaignNameById, visibleMedias]);

  // Collection card previews use a different (video-aware) URL than the
  // gallery thumbnail — non-Cloudinary videos return their raw URL here so
  // the card can render a <video> element instead of a broken <img>.
  const collectionPreviewUrlByMediaId = useMemo(() => {
    const map = new Map<string, string>();
    visibleMedias.forEach((m) => {
      const mediaId = typeof m.media_id === 'string' ? m.media_id : '';
      if (!mediaId) return;
      const url = getCollectionCardPreviewUrlFromMedia(m);
      if (!url) return;
      map.set(mediaId, url);
    });
    return map;
  }, [visibleMedias]);

  useEffect(() => {
    setSelectedItems([]);
    if (!previewMediaId?.trim()) {
      setSelectedContent(null);
      setDeepLinkMediaById({});
    }
  }, [selectedFolderId, previewMediaId]);

  useEffect(() => {
    setIsPreviewVideoPlaying(false);
  }, [selectedContent?.mediaId]);

  const getMediaForContentItemId = useCallback(
    (id: number) => {
      const idx = id - 1;
      if (idx < 0 || idx >= visibleMedias.length) return null;
      return visibleMedias[idx] ?? null;
    },
    [visibleMedias]
  );

  const resolveMediaForContentItem = useCallback(
    (item: ContentItem | null): ContentHubMedia | null => {
      if (!item?.mediaId) return null;
      const row = visibleMedias.find((m) => m.media_id === item.mediaId);
      if (row) return row;
      return deepLinkMediaById[item.mediaId] ?? null;
    },
    [visibleMedias, deepLinkMediaById]
  );

  const selectedContentMedia = useMemo(() => {
    if (!selectedContent) return null;
    return resolveMediaForContentItem(selectedContent);
  }, [resolveMediaForContentItem, selectedContent]);

  const selectedContentUrl = useMemo(() => {
    const url = selectedContentMedia?.contenthub_url;
    return typeof url === 'string' && url ? url : null;
  }, [selectedContentMedia]);

  useEffect(() => {
    const trimmed = previewMediaId?.trim();
    if (!trimmed) {
      setSelectedContent(null);
      setDeepLinkMediaById({});
      return;
    }
    const mediaId: string = trimmed;

    const fromGrid = contentItems.find((c) => c.mediaId === mediaId);
    if (fromGrid) {
      setSelectedContent(fromGrid);
      return;
    }

    // Wait for the gallery's first page so we can resolve the media via the existing
    // grid before triggering a dedicated lookup. Paths loading is intentionally NOT a
    // dependency: it can be slow and is only needed for the breadcrumb (which can hydrate
    // later without delaying the preview open).
    if (isLoadingContentHubMedias) return;

    let cancelled = false;

    async function fetchDeepLinkedMedia() {
      const token = getTokenFromStorage();
      if (!token) return;
      try {
        const page = await getContentHubSearch(token, {
          text: mediaId,
          pageNumber: 0,
          pageSize: 24,
        });
        if (cancelled) return;
        if (page?.isExpired) {
          logout();
          return;
        }
        const medias = Array.isArray(page?.medias) ? page.medias : [];
        const hit = medias.find((m) => typeof m.media_id === 'string' && m.media_id === mediaId);
        if (!hit) {
          if (!cancelled) {
            showError(i18n.t('contentHub.preview.deepLinkNotFound'));
            navigate(getPathForPage('content-hub'), { replace: true });
          }
          return;
        }
        if (cancelled) return;
        setDeepLinkMediaById((prev) => ({ ...prev, [mediaId]: hit }));
        const idx = visibleMedias.findIndex((m) => m.media_id === mediaId);
        const baseItem = mapContentHubMediaToContentItem(hit, Math.max(0, idx));
        const campaignId = typeof hit.campaign_id === 'string' ? hit.campaign_id : '';
        const campaignName = campaignId ? campaignNameById.get(campaignId) : undefined;
        if (cancelled) return;
        setSelectedContent({
          ...baseItem,
          campaign: campaignName || baseItem.campaign,
        });
      } catch {
        if (!cancelled) {
          showError(i18n.t('contentHub.preview.deepLinkNotFound'));
          navigate(getPathForPage('content-hub'), { replace: true });
        }
      }
    }

    void fetchDeepLinkedMedia();
    return () => {
      cancelled = true;
    };
  }, [
    previewMediaId,
    contentItems,
    isLoadingContentHubMedias,
    navigate,
    campaignNameById,
    visibleMedias,
  ]);

  const getDownloadFilename = useCallback((url: string, filename: string | undefined) => {
    if (filename) return filename;
    try {
      const u = new URL(url);
      const last = u.pathname.split('/').filter(Boolean).pop();
      return last || 'download';
    } catch {
      return 'download';
    }
  }, []);

  const apiHost = useMemo(() => getUrlHost(import.meta.env.VITE_BEATLY_API_URL || ''), []);

  const downloadOne = useCallback(async (url: string, filename: string, token?: string | null) => {
    try {
      const headers: Record<string, string> = {};
      if (token && apiHost && getUrlHost(url) === apiHost) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename || 'download';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      // Cloudinary doesn't allow cross-origin fetch; use its fl_attachment flag instead.
      const cloudinaryAttachmentUrl = getCloudinaryAttachmentUrl(url, filename);
      if (cloudinaryAttachmentUrl) {
        triggerBrowserDownload(cloudinaryAttachmentUrl);
        return;
      }

      // Last resort (may open in a tab depending on browser/CORS/headers).
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      a.rel = 'noopener noreferrer';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, [apiHost]);

  // Define this here (after gallery query) so it can read derived contentCountTotal/contentHubMedias.
  const favoritesCountForLabel = useMemo(() => {
    if (typeof favoritesCountTotal === 'number') return favoritesCountTotal;
    if (showFavoritesOnly) {
      if (typeof contentCountTotal === 'number') return contentCountTotal;
      return contentHubMedias.length;
    }
    return favorites.length;
  }, [showFavoritesOnly, contentCountTotal, contentHubMedias.length, favorites.length, favoritesCountTotal]);

  const campaignTreeChildren = useMemo<TreeNode[]>(() => {
    if (isLoadingContentHubPaths) return [];

    const byCampaignId = new Map<
      string,
      {
        campaignName: string;
        campaignYear: number | null | undefined;
        campaignCountryCodes: string[];
        collaborations: Map<string, { name: string; handle: string; avatarUrl?: string }>;
      }
    >();

    contentHubPathRows.forEach((row) => {
      if (!row.campaign_id) return;
      const existing = byCampaignId.get(row.campaign_id) ?? {
        campaignName: row.campaign_name,
        campaignYear: row.campaign_year ?? undefined,
        campaignCountryCodes: [] as string[],
        collaborations: new Map<string, { name: string; handle: string; avatarUrl?: string }>(),
      };

      existing.campaignName = existing.campaignName || row.campaign_name;
      if (existing.campaignYear == null && row.campaign_year != null) {
        existing.campaignYear = row.campaign_year;
      }
      const rowCodes = normalizePathRowCountryCodes(row);
      if (rowCodes.length > 0) {
        existing.campaignCountryCodes = [...new Set([...existing.campaignCountryCodes, ...rowCodes])].sort();
      }
      if (row.collaboration_id) {
        existing.collaborations.set(row.collaboration_id, {
          name: row.influencer_name,
          handle: row.influencer_handle,
          avatarUrl: row.influencer_mugshot_url,
        });
      }
      byCampaignId.set(row.campaign_id, existing);
    });

    const campaigns = Array.from(byCampaignId.entries()).map(([campaignId, data]) => {
      const collaborationNodes: TreeNode[] = Array.from(data.collaborations.entries())
        .map(([collabId, c]) => {
          const label = c.handle ? `${c.name} (${c.handle})` : c.name;
          return {
            id: collabId,
            label,
            type: 'creator' as const,
            avatarUrl: c.avatarUrl,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      return {
        id: campaignId,
        label: data.campaignName || campaignId,
        type: 'campaign' as const,
        expanded: expandedNodes[campaignId] ?? false,
        children: collaborationNodes,
        campaignYear: data.campaignYear ?? null,
        campaignCountryCodes: data.campaignCountryCodes,
      } satisfies TreeNode;
    });

    return campaigns.sort((a, b) => {
      if (campaignFolderSort === 'name') {
        return a.label.localeCompare(b.label);
      }
      const ya = a.campaignYear ?? -Infinity;
      const yb = b.campaignYear ?? -Infinity;
      if (yb !== ya) return yb - ya;
      return a.label.localeCompare(b.label);
    });
  }, [campaignFolderSort, contentHubPathRows, expandedNodes, isLoadingContentHubPaths]);

  const availableCampaignCountryCodes = useMemo(() => {
    const acc = new Set<string>();
    contentHubPathRows.forEach((row) => {
      normalizePathRowCountryCodes(row).forEach((c) => acc.add(c));
    });
    return [...acc].sort();
  }, [contentHubPathRows]);

  useEffect(() => {
    if (
      selectedCampaignCountryCode != null &&
      !availableCampaignCountryCodes.includes(selectedCampaignCountryCode)
    ) {
      setSelectedCampaignCountryCode(null);
    }
  }, [availableCampaignCountryCodes, selectedCampaignCountryCode]);

  const countryFilteredCampaignTreeChildren = useMemo<TreeNode[]>(() => {
    if (selectedCampaignCountryCode == null) return campaignTreeChildren;
    return campaignTreeChildren.filter((node) => {
      const codes = node.campaignCountryCodes ?? [];
      return codes.includes(selectedCampaignCountryCode);
    });
  }, [campaignTreeChildren, selectedCampaignCountryCode]);

  const filteredCampaignTreeChildren = useMemo<TreeNode[]>(() => {
    const raw = searchQuery.trim();
    if (!raw) return countryFilteredCampaignTreeChildren;

    const yearsInQuery = extractYearsFromQuery(raw);
    const textPart = textQueryWithoutYears(raw).toLowerCase().replace(/^@/, '');
    const hasYears = yearsInQuery.length > 0;
    const hasText = textPart.length > 0;

    const matchesText = (text: string) => {
      const t = text.toLowerCase();
      const q = textPart;
      return t.includes(q) || t.replace(/@/g, '').includes(q);
    };

    const campaignMatchesYear = (campaign: TreeNode) =>
      campaign.campaignYear != null && yearsInQuery.includes(campaign.campaignYear);

    const result: TreeNode[] = [];
    countryFilteredCampaignTreeChildren.forEach((campaign) => {
      const campaignMatchesText = hasText && matchesText(campaign.label);
      const campaignMatches = campaignMatchesText || (hasYears && campaignMatchesYear(campaign));

      const childrenFiltered = (campaign.children ?? []).filter((c) => hasText && matchesText(c.label));

      if (campaignMatches) {
        result.push({
          ...campaign,
          expanded: true,
        });
        return;
      }

      if (childrenFiltered.length > 0) {
        result.push({
          ...campaign,
          expanded: true,
          children: childrenFiltered,
        });
      }
    });

    return result;
  }, [countryFilteredCampaignTreeChildren, searchQuery]);

  const collectionTreeChildren = useMemo<TreeNode[]>(() => {
    const nodes = collections
      .map((c) => ({
        id: c.collection_id,
        label: c.name,
        type: 'collection' as const,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const q = searchQuery.trim().toLowerCase().replace(/^@/, '');
    if (!q) return nodes;
    return nodes.filter((n) => n.label.toLowerCase().includes(q));
  }, [collections, searchQuery]);

  const hasSidebarSearch =
    searchQuery.trim().length > 0 || selectedCampaignCountryCode != null;

  const treeData = useMemo<TreeNode[]>(
    () => [
      {
        id: 'campaigns',
        label: i18n.t('contentHub.sidebar.campaignsSection'),
        type: 'campaign',
        expanded: hasSidebarSearch ? true : (expandedNodes['campaigns'] ?? true),
        children: filteredCampaignTreeChildren,
      },
      {
        id: 'collections',
        label: i18n.t('contentHub.sidebar.myCollectionsSection'),
        type: 'collection',
        expanded: hasSidebarSearch ? true : (expandedNodes['collections'] ?? true),
        children: collectionTreeChildren,
      },
    ],
    [collectionTreeChildren, expandedNodes, filteredCampaignTreeChildren, hasSidebarSearch]
  );

  // Helper functions
  const getDaysUntilExpiry = (expiryDate: string): number => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get the earliest expiry date across all rights (ignoring indefinite ones)
  const getEarliestExpiry = (item: ContentItem): string | null => {
    const dated = item.rights.filter(r => r.expiry !== null).map(r => r.expiry as string);
    if (dated.length === 0) return null;
    return dated.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
  };

  const isExpiringSoon = (item: ContentItem): boolean => {
    const earliest = getEarliestExpiry(item);
    if (!earliest) return false;
    const daysUntil = getDaysUntilExpiry(earliest);
    return daysUntil >= 0 && daysUntil <= 30;
  };

  const isExpired = (item: ContentItem): boolean => {
    const earliest = getEarliestExpiry(item);
    if (!earliest) return false;
    const daysUntil = getDaysUntilExpiry(earliest);
    return daysUntil < 0;
  };

  const isRightExpired = (expiry: string | null): boolean => {
    if (!expiry) return false;
    return getDaysUntilExpiry(expiry) < 0;
  };

  const isRightExpiringSoon = (expiry: string | null): boolean => {
    if (!expiry) return false;
    const days = getDaysUntilExpiry(expiry);
    return days >= 0 && days <= 30;
  };

  const getRightStatusColor = (expiry: string | null): string => {
    if (!expiry) return 'text-gray-500';
    if (isRightExpired(expiry)) return 'text-[#ff5635]';
    if (isRightExpiringSoon(expiry)) return 'text-amber-500';
    return 'text-gray-500';
  };

  const getRightStatusText = (expiry: string | null): string => {
    if (!expiry) return 'Indefinite';
    if (isRightExpired(expiry)) return 'Expired';
    if (isRightExpiringSoon(expiry)) return `${getDaysUntilExpiry(expiry)}d left`;
    return expiry;
  };

  const rightDescriptions: Record<string, string> = {
    republishing: 'The brand and Beatly have the right to republish campaign material on their own social media platforms, website, and newsletter.',
    sponsorship: 'The brand may use material in paid digital advertising for the specified period after the campaign ends.',
    editing: 'The brand has the right to edit, cut, create variations, and change formats (e.g., 16:9 → 9:16).',
  };

  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  // Feature 2: Filtered content using useMemo
  const filteredContent = useMemo(() => {
    return contentItems.filter(item => {
      // Type filter
      if (activeFilters.type !== 'all' && item.type !== activeFilters.type) {
        return false;
      }
      
      // Campaign filter
      if (activeFilters.campaign !== 'all') {
        const media = visibleMedias[item.id - 1];
        const campaignId = typeof media?.campaign_id === 'string' ? media.campaign_id : null;
        if (!campaignId || campaignId !== activeFilters.campaign) return false;
      }
      
      // Creator filter
      if (activeFilters.creator !== 'all' && item.creator !== activeFilters.creator) {
        return false;
      }
      
      // Expiring days filter (Feature 5: Rights expiration)
      if (activeFilters.expiringDays > 0) {
        const earliest = getEarliestExpiry(item);
        if (!earliest) return false;
        const daysUntil = getDaysUntilExpiry(earliest);
        if (daysUntil < 0 || daysUntil > activeFilters.expiringDays) {
          return false;
        }
      }
      
      return true;
    });
  }, [activeFilters, contentItems, visibleMedias]);

  useEffect(() => {
    const visibleIds = new Set(filteredContent.map((i) => i.id));
    setSelectedItems((prev) => {
      const next = prev.filter((id) => visibleIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredContent]);

  const uniqueCreators = useMemo(() => {
    return Array.from(new Set(contentItems.map(item => item.creator)));
  }, [contentItems]);

  // Feature 5: Count expiring content
  const expiringCount = useMemo(() => {
    return contentItems.filter(item => isExpiringSoon(item)).length;
  }, [contentItems]);

  // Feature 1: Multi-select handlers
  const toggleItemSelection = useCallback((id: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id],
    );
  }, []);

  const selectAll = () => {
    setSelectedItems(filteredContent.map((item) => item.id));
  };

  const deselectAll = () => {
    setSelectedItems([]);
  };

  // Stable tile hover handlers so memoized <ContentTile> only re-renders the
  // previously-hovered and newly-hovered tiles.
  const handleTileMouseEnter = useCallback((id: number) => {
    setHoveredContent(id);
  }, []);
  const handleTileMouseLeave = useCallback(() => {
    setHoveredContent(null);
  }, []);

  const handleCollectionSelect = useCallback(
    (collection: ContentHubCollection) => {
      const isSelected = selectedTreeNodeId === collection.collection_id;
      if (isSelected) {
        // Deselect collection = show all content (team root).
        setSelectedTreeNodeId('campaigns');
        setSelectedFolderId(defaultTeamFolderId);
        return;
      }
      setSelectedTreeNodeId(collection.collection_id);
      setSelectedFolderId(collection.collection_id);
    },
    [defaultTeamFolderId, selectedTreeNodeId],
  );

  const handleCollectionEdit = useCallback((collection: ContentHubCollection) => {
    setEditingCollectionId(collection.collection_id);
    setEditCollectionName(collection.name);
    setEditCollectionDescription(collection.description ?? '');
    const v = normalizeCollectionVisibility(collection.visibility);
    setEditCollectionVisibility(v);
    setEditCollectionOriginalVisibility(v);
    setEditPictureFile(null);
    setIsEditCollectionOpen(true);
  }, []);

  const handleCollectionPreviewLoaded = useCallback(
    (collectionId: string, url: string) => {
      setCollectionPreviewById((prev) => {
        if (prev[collectionId]) return prev;
        return { ...prev, [collectionId]: url };
      });
    },
    [],
  );

  // Content Detail Navigation
  const filmstripRef = useRef<HTMLDivElement>(null);
  
  const currentContentIndex = useMemo(() => {
    if (!selectedContent?.mediaId) return -1;
    return filteredContent.findIndex((item) => item.mediaId === selectedContent.mediaId);
  }, [selectedContent, filteredContent]);

  const navigateContent = useCallback(
    (direction: 'prev' | 'next') => {
      if (currentContentIndex === -1) return;
      const newIndex = direction === 'prev' ? currentContentIndex - 1 : currentContentIndex + 1;
      if (newIndex >= 0 && newIndex < filteredContent.length) {
        const nextItem = filteredContent[newIndex];
        setSelectedContent(nextItem);
        setActiveTooltip(null);
        if (nextItem.mediaId) {
          navigate(getPathForPage('content-hub', { contentHubMediaId: nextItem.mediaId }), { replace: true });
        }
      }
    },
    [currentContentIndex, filteredContent, navigate]
  );

  const hasPrev = currentContentIndex > 0;
  const hasNext = currentContentIndex >= 0 && currentContentIndex < filteredContent.length - 1;

  useEffect(() => {
    if (!selectedContent) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateContent('prev');
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateContent('next');
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeContentPreview();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedContent, navigateContent, closeContentPreview]);

  // Scroll filmstrip to keep active thumbnail visible
  useEffect(() => {
    if (selectedContent && filmstripRef.current) {
      const thumb = filmstripRef.current.querySelector('[data-active="true"]');
      if (thumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedContent]);

  const bulkDownload = () => {
    if (selectedItems.length === 0 || isDownloading) return;
    const token = getTokenFromStorage();

    const items = filteredContent.filter((i) => selectedItems.includes(i.id));
    const medias = items
      .map((i) => getMediaForContentItemId(i.id))
      .filter((m): m is ContentHubMedia => Boolean(m));

    setIsDownloading(true);
    medias
      .reduce((p, m) => {
        const url = typeof m.contenthub_url === 'string' ? m.contenthub_url : '';
        if (!url) return p;
        const filename = getDownloadFilename(url, formatContentHubDisplayFilename(m));
        return p.then(() => downloadOne(url, filename, token));
      }, Promise.resolve())
      .finally(() => {
        setIsDownloading(false);
        setSelectedItems([]);
      });
  };

  const downloadSelectedContent = () => {
    if (!selectedContent || isDownloading) return;
    const token = getTokenFromStorage();
    const media = resolveMediaForContentItem(selectedContent);
    if (!media) return;

    const url = typeof media.contenthub_url === 'string' ? media.contenthub_url : '';
    if (!url) return;
    const filename = getDownloadFilename(url, formatContentHubDisplayFilename(media));

    setIsDownloading(true);
    downloadOne(url, filename, token).finally(() => setIsDownloading(false));
  };

  const selectedCollection = useMemo(() => {
    const id = selectedTreeNodeId;
    if (!id) return null;
    return collections.find((c) => c.collection_id === id) ?? null;
  }, [collections, selectedTreeNodeId]);

  const selectedCollectionId = selectedCollection?.collection_id ?? null;

  const getSelectedMediaIds = useCallback(() => {
    const ids = selectedItems
      .map((id) => getMediaForContentItemId(id))
      .map((m) => (m && typeof m.media_id === 'string' ? m.media_id : null))
      .filter(Boolean) as string[];
    return Array.from(new Set(ids));
  }, [getMediaForContentItemId, selectedItems]);

  const bulkAddToCollection = () => {
    if (selectedItems.length === 0) return;
    const mediaIds = getSelectedMediaIds();
    if (mediaIds.length === 0) return;
    setCollectionActionMediaIds(mediaIds);
    setCollectionActionSource('bulk');
    setSelectedAddToCollectionIds([]);
    setIsAddToCollectionOpen(true);
  };

  const addSelectedContentToCollection = () => {
    const mediaId = selectedContentMedia && typeof selectedContentMedia.media_id === 'string' ? selectedContentMedia.media_id : null;
    if (!mediaId) return;
    setCollectionActionMediaIds([mediaId]);
    setCollectionActionSource('single');
    setSelectedAddToCollectionIds([]);
    setIsAddToCollectionOpen(true);
  };

  const confirmAddToCollection = async () => {
    const token = getTokenFromStorage();
    if (!token) return;
    if (selectedAddToCollectionIds.length === 0) return;

    const mediaIds = collectionActionMediaIds;
    if (mediaIds.length === 0) return;
    if (isSavingCollection) return;

    setIsSavingCollection(true);
    try {
      for (const collectionId of selectedAddToCollectionIds) {
        await addContentHubCollectionMedias(token, collectionId, mediaIds);
      }

      const firstMediaId = mediaIds[0] ?? null;
      const previewUrl = firstMediaId ? collectionPreviewUrlByMediaId.get(firstMediaId) ?? null : null;
      if (previewUrl) {
        setCollectionPreviewById((prev) => {
          const next = { ...prev };
          selectedAddToCollectionIds.forEach((collectionId) => {
            const hasServerPicture = Boolean(collections.find((c) => c.collection_id === collectionId)?.picture_url);
            if (hasServerPicture) return;
            if (next[collectionId]) return;
            next[collectionId] = previewUrl;
          });
          return next;
        });
      }

      setIsAddToCollectionOpen(false);
      if (collectionActionSource === 'bulk') {
        setSelectedItems([]);
      }
      // Invalidate the gallery cache for the collections we just modified so the
      // newly added items appear when the user opens any of those collections.
      // Predicate-based so we only refetch the affected `collection`-scoped
      // gallery views and leave the current source view (campaigns/team) alone.
      const affectedCollectionIds = new Set(selectedAddToCollectionIds);
      await queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          if (!Array.isArray(k)) return false;
          return (
            k[0] === 'contenthub' &&
            k[1] === 'medias' &&
            k[3] === 'collection' &&
            typeof k[4] === 'string' &&
            affectedCollectionIds.has(k[4] as string)
          );
        },
      });
      await refreshCollections();
    } catch {
      // Keep UI as-is on error.
    } finally {
      setIsSavingCollection(false);
    }
  };

  const bulkRemoveFromCollection = async () => {
    const token = getTokenFromStorage();
    if (!token) return;
    if (!selectedCollectionId) return;

    const mediaIds = getSelectedMediaIds();
    if (mediaIds.length === 0) return;
    if (isSavingCollection) return;

    // Optimistically drop the removed medias from the cached collection gallery
    // so they disappear from view immediately. Restored on error below.
    const mediaIdSet = new Set(mediaIds);
    const removedSnapshot = queryClient.getQueryData<InfiniteData<ContentHubMediaPage>>(galleryQueryKey);
    queryClient.setQueryData<InfiniteData<ContentHubMediaPage>>(galleryQueryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((p) => ({
          ...p,
          medias: Array.isArray(p?.medias) ? p.medias.filter((m) => !mediaIdSet.has(m.media_id)) : p.medias,
        })),
      };
    });

    setIsSavingCollection(true);
    try {
      await removeContentHubCollectionMedias(token, selectedCollectionId, mediaIds);
      setSelectedItems([]);
      await refreshCollections();
    } catch {
      // Revert the optimistic gallery update on error.
      if (removedSnapshot !== undefined) {
        queryClient.setQueryData<InfiniteData<ContentHubMediaPage>>(galleryQueryKey, removedSnapshot);
      }
    } finally {
      setIsSavingCollection(false);
    }
  };

  // Feature 3: Favorites handlers
  const toggleFavorite = useCallback(async (item: ContentItem, event: React.MouseEvent) => {
    event.stopPropagation();
    const token = getTokenFromStorage();
    if (!token) return;
    if (!item.mediaId) return;
    const wasFavorite = favorites.includes(item.mediaId) || activeFilters.showFavoritesOnly;
    const mediaId = item.mediaId;

    setFavorites((prev) => (wasFavorite ? prev.filter((x) => x !== mediaId) : [...prev, mediaId]));
    setFavoritesCountOverride((prev) => {
      const base = prev ?? favoritesCountQuery.data ?? null;
      if (base === null) return prev;
      return wasFavorite ? Math.max(0, base - 1) : base + 1;
    });

    // When un-favoriting from the favorites view, drop the row optimistically from
    // the cached infinite query so it disappears immediately.
    let removedSnapshot: InfiniteData<ContentHubMediaPage> | undefined;
    if (activeFilters.showFavoritesOnly && wasFavorite) {
      removedSnapshot = queryClient.getQueryData<InfiniteData<ContentHubMediaPage>>(galleryQueryKey);
      queryClient.setQueryData<InfiniteData<ContentHubMediaPage>>(galleryQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            medias: Array.isArray(p?.medias) ? p.medias.filter((m) => m.media_id !== mediaId) : p.medias,
          })),
        };
      });
    }

    try {
      if (wasFavorite) {
        await unlikeContentHubMedia(token, mediaId);
      } else {
        await likeContentHubMedia(token, mediaId);
      }
    } catch {
      // Revert optimistic updates on error.
      setFavorites((prev) => (wasFavorite ? [...prev, mediaId] : prev.filter((x) => x !== mediaId)));
      setFavoritesCountOverride((prev) => {
        if (prev === null) return prev;
        return wasFavorite ? prev + 1 : Math.max(0, prev - 1);
      });
      if (removedSnapshot !== undefined) {
        queryClient.setQueryData<InfiniteData<ContentHubMediaPage>>(galleryQueryKey, removedSnapshot);
      }
    }
  }, [activeFilters.showFavoritesOnly, favorites, favoritesCountQuery.data, galleryQueryKey, queryClient]);


  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !(prev[nodeId] ?? true),
    }));
  };

  const renderTreeNode = (node: TreeNode, level: number = 0) => {
    const hasChildren = Boolean(node.children && node.children.length > 0);
    const isSidebarSectionRoot = node.id === 'campaigns' || node.id === 'collections';
    const showExpandChevron = isSidebarSectionRoot || hasChildren;
    const isSelected = node.id === selectedTreeNodeId;
    const FolderIcon = node.expanded ? FolderOpen : Folder;
    const isCampaignFolderRow = node.type === 'campaign' && node.id !== 'campaigns';

    return (
      <div key={node.id}>
        <button
          type="button"
          onClick={() => {
            setSelectedTreeNodeId(node.id);
            if (node.id === 'campaigns' || node.id === 'collections') {
              if (defaultTeamFolderId) setSelectedFolderId(defaultTeamFolderId);
              toggleNode(node.id);
              return;
            }
            setSelectedFolderId(node.id);
            if (hasChildren) toggleNode(node.id);
          }}
          aria-expanded={showExpandChevron ? Boolean(node.expanded) : undefined}
          className={`flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded-lg transition-colors ${
            isSelected ? 'bg-gray-200/60' : 'hover:bg-gray-200/60'
          }`}
          style={{ paddingLeft: `${level * 16 + 12}px` }}
        >
          {showExpandChevron ? (
            node.expanded ? (
              <ChevronDown className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-gray-600' : 'text-gray-400'}`} />
            ) : (
              <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-gray-600' : 'text-gray-400'}`} />
            )
          ) : (
            <div className="w-4" />
          )}
          {node.type === 'creator' ? (
            node.avatarUrl ? (
              <img
                src={node.avatarUrl}
                alt=""
                className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <User className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-gray-700' : 'text-gray-500'}`} />
            )
          ) : (
            <FolderIcon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-gray-700' : 'text-gray-500'}`} />
          )}
          {isCampaignFolderRow ? (
            <>
              <span
                className={`truncate min-w-0 flex-1 text-left ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-600'}`}
                title={node.label}
              >
                {node.label}
              </span>
              {node.campaignYear != null ? (
                <span
                  className={`shrink-0 text-xs tabular-nums ${isSelected ? 'text-gray-600' : 'text-gray-400'}`}
                  aria-label={i18n.t('contentHub.sidebar.campaignYearAria', { year: node.campaignYear })}
                >
                  {node.campaignYear}
                </span>
              ) : null}
            </>
          ) : (
            <span
              className={`truncate ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-600'}`}
              title={node.label}
            >
              {node.label}
            </span>
          )}
        </button>
        {node.expanded && node.children && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (activeFilters.type !== 'all') count++;
    if (activeFilters.campaign !== 'all') count++;
    if (activeFilters.creator !== 'all') count++;
    if (activeFilters.expiringDays > 0) count++;
    if (activeFilters.kind !== 'all' && !activeFilters.showFavoritesOnly) count++;
    return count;
  }, [activeFilters]);

  return (
    <div className="flex h-full bg-[#f8f9fa] overflow-hidden overscroll-none">
      <PanelGroup
        direction="horizontal"
        autoSaveId="content-hub-browse-main"
        className="flex flex-1 min-h-0 min-w-0"
        onLayout={(sizes) => {
          const first = sizes[0];
          if (typeof first === 'number' && first < 0.5) {
            setIsSidebarCollapsed(true);
          } else if (typeof first === 'number') {
            setIsSidebarCollapsed(false);
          }
        }}
      >
        <Panel
          ref={browsePanelRef}
          collapsible
          collapsedSize={0}
          defaultSize={22}
          minSize={15}
          maxSize={45}
          className="min-h-0 min-w-0"
          onCollapse={() => setIsSidebarCollapsed(true)}
          onExpand={() => setIsSidebarCollapsed(false)}
        >
          <SecondarySidebar width="w-full" className="min-w-0">
          <SecondarySidebarHeader>
            <h2 className="text-gray-900 mb-3 text-sm">{i18n.t('contentHub.sidebar.browseHeading')}</h2>
            <SecondarySidebarSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={i18n.t('contentHub.sidebar.searchCampaignsPlaceholder')}
              clearSearchAriaLabel={i18n.t('contentHub.sidebar.clearSearchAria')}
            />
            <label htmlFor="content-hub-campaign-sort" className="sr-only">
              {i18n.t('contentHub.sidebar.sortCampaignsAria')}
            </label>
            <select
              id="content-hub-campaign-sort"
              className="mt-2 w-full px-2 py-1.5 text-xs bg-white border border-gray-200 rounded-lg text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#be43ff]/50 focus:border-[#be43ff]/40"
              value={campaignFolderSort}
              onChange={(e) => setCampaignFolderSort(e.target.value as 'year' | 'name')}
              aria-label={i18n.t('contentHub.sidebar.sortCampaignsAria')}
            >
              <option value="year">{i18n.t('contentHub.sidebar.sortByYear')}</option>
              <option value="name">{i18n.t('contentHub.sidebar.sortByName')}</option>
            </select>
            {availableCampaignCountryCodes.length > 0 ? (
              <div
                role="group"
                aria-label={i18n.t('contentHub.sidebar.countryFilterAria')}
                className="mt-2 flex flex-wrap gap-1.5"
              >
                <button
                  type="button"
                  onClick={() => setSelectedCampaignCountryCode(null)}
                  aria-pressed={selectedCampaignCountryCode === null}
                  className={`px-2 py-1 text-xs rounded-md border transition-colors focus:outline-none focus:ring-1 focus:ring-[#be43ff]/50 ${
                    selectedCampaignCountryCode === null
                      ? 'bg-[#be43ff]/12 border-[#be43ff]/40 text-[#be43ff] font-medium'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {i18n.t('contentHub.sidebar.allCountries')}
                </button>
                {availableCampaignCountryCodes.map((code) => {
                  const label = formatCountryCodeLabel(code, i18n.language || 'en');
                  const isPressed = selectedCampaignCountryCode === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setSelectedCampaignCountryCode(code)}
                      aria-pressed={isPressed}
                      aria-label={i18n.t('contentHub.sidebar.countryChipAria', { country: label })}
                      className={`px-2 py-1 text-xs rounded-md border transition-colors focus:outline-none focus:ring-1 focus:ring-[#be43ff]/50 ${
                        isPressed
                          ? 'bg-[#be43ff]/12 border-[#be43ff]/40 text-[#be43ff] font-medium'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </SecondarySidebarHeader>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2">
            {treeData.map(node => renderTreeNode(node))}
          </div>
        </SecondarySidebar>
        </Panel>

        <PanelResizeHandle
          className="group relative flex w-2 shrink-0 items-stretch justify-center bg-transparent outline-none transition-colors hover:bg-gray-200/80 focus-visible:ring-2 focus-visible:ring-[#be43ff] focus-visible:ring-offset-1 data-[resize-handle-state=drag]:bg-gray-200/90"
          aria-label={i18n.t('contentHub.sidebar.resizeBrowseAria')}
          title={i18n.t('contentHub.sidebar.resizeBrowseTitle')}
          hitAreaMargins={{ coarse: 10, fine: 6 }}
        />

        <Panel defaultSize={78} minSize={55} className="min-h-0 min-w-0 flex flex-col">
      <div className="flex h-full min-h-0 flex flex-col p-2.5 pl-2.5 min-w-0 transition-all duration-300">
        <div className="flex-1 flex flex-col rounded-2xl bg-white overflow-hidden border border-gray-200/80 shadow-sm min-h-0">
        {/* Top Bar */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const p = browsePanelRef.current;
                  if (!p) return;
                  if (p.isCollapsed()) {
                    p.expand(15);
                  } else {
                    p.collapse();
                  }
                }}
                className="text-gray-600 hover:text-gray-900"
                aria-label={
                  isSidebarCollapsed
                    ? i18n.t('contentHub.sidebar.toggleBrowseExpandAria')
                    : i18n.t('contentHub.sidebar.toggleBrowseCollapseAria')
                }
              >
                {isSidebarCollapsed ? (
                  <PanelLeft className="w-5 h-5" />
                ) : (
                  <PanelLeftClose className="w-5 h-5" />
                )}
              </Button>
              <h1 className="text-gray-900">Content Hub</h1>
              
              {/* Feature 5: Expiring Alert Badge */}
              {expiringCount > 0 && (
                <Badge 
                  className="bg-amber-100 text-amber-800 border-amber-300 cursor-pointer hover:bg-amber-200"
                  onClick={() => setActiveFilters({ ...activeFilters, expiringDays: 30 })}
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {expiringCount} expiring soon
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Feature 3: Favorites filter */}
              <Button 
                variant={activeFilters.showFavoritesOnly ? "default" : "outline"}
                size="sm"
                className={activeFilters.showFavoritesOnly ? "bg-purple-600 hover:bg-purple-700 text-white" : "text-gray-700"}
                onClick={() => {
                  const next = !activeFilters.showFavoritesOnly;
                  setActiveFilters({ ...activeFilters, showFavoritesOnly: next });
                  if (!next) {
                    // When leaving Favorites, always go back to "All Content" (team root).
                    setSelectedTreeNodeId('campaigns');
                    setSelectedFolderId(defaultTeamFolderId ?? null);
                  }
                }}
              >
                <Star className={`w-4 h-4 mr-2 ${activeFilters.showFavoritesOnly ? 'fill-current' : ''}`} />
                {i18n.t('contentHub.favorites.labelWithCount', { count: favoritesCountForLabel })}
              </Button>
              
              {/* Feature 2: Filter Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className={`text-gray-700 ${activeFilterCount > 0 ? 'border-purple-500 text-purple-700' : ''}`}
                onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
              </Button>
            </div>
          </div>
          
          {/* Feature 2: Filter Panel */}
          {isFilterPanelOpen && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-2">Content Type</label>
                  <select 
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={activeFilters.type}
                    onChange={(e) => setActiveFilters({ ...activeFilters, type: e.target.value as 'all' | 'image' | 'video' })}
                  >
                    <option value="all">All Types</option>
                    <option value="image">Images Only</option>
                    <option value="video">Videos Only</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <label className="block text-xs text-gray-600">{i18n.t('contentHub.filters.kindLabel')}</label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={i18n.t('contentHub.filters.kindTooltip')}
                          className="inline-flex items-center text-gray-400 hover:text-gray-600"
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs whitespace-normal text-left">
                        {i18n.t('contentHub.filters.kindTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <select
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    value={activeFilters.showFavoritesOnly ? 'all' : activeFilters.kind}
                    onChange={(e) => setActiveFilters({ ...activeFilters, kind: e.target.value as ContentHubKind })}
                    disabled={activeFilters.showFavoritesOnly}
                    title={activeFilters.showFavoritesOnly ? i18n.t('contentHub.filters.kindFavoritesDisabled') : undefined}
                  >
                    <option value="all">{i18n.t('contentHub.filters.kindAll')}</option>
                    <option value="raw">{i18n.t('contentHub.filters.kindRaw')}</option>
                    <option value="final">{i18n.t('contentHub.filters.kindFinal')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-2">Campaign</label>
                  <select 
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={activeFilters.campaign}
                    onChange={(e) => setActiveFilters({ ...activeFilters, campaign: e.target.value })}
                  >
                    <option value="all">All Campaigns</option>
                    {availableCampaigns.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-2">Creator</label>
                  <select 
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={activeFilters.creator}
                    onChange={(e) => setActiveFilters({ ...activeFilters, creator: e.target.value })}
                  >
                    <option value="all">All Creators</option>
                    {uniqueCreators.map(creator => (
                      <option key={creator} value={creator}>{creator}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Showing {filteredContent.length} of {contentItems.length} items
                </p>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setActiveFilters({ type: 'all', campaign: 'all', creator: 'all', expiringDays: 0, showFavoritesOnly: false, kind: 'all' })}
                >
                  Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Feature 1: Bulk Action Bar */}
        {selectedItems.length > 0 && (
          <div className="bg-purple-600 text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="font-medium">{selectedItems.length} items selected</span>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  onClick={selectAll}
                >
                  Select All ({filteredContent.length})
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-white hover:bg-white/10"
                  onClick={deselectAll}
                >
                  Deselect All
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline" 
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={bulkDownload}
                disabled={isDownloading}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              {selectedCollection ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  onClick={bulkRemoveFromCollection}
                  disabled={isSavingCollection || isLoadingCollections}
                >
                  <FolderMinus className="w-4 h-4 mr-2" />
                  Remove from Collection
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  onClick={bulkAddToCollection}
                  disabled={isSavingCollection || isLoadingCollections}
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Add to Collection
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Content Area */}
        <div
          ref={galleryScrollRef}
          onScroll={handleGalleryScroll}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        >
          {/* Collections Section */}
          <div className="p-6 pb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-gray-900">{i18n.t('contentHub.sidebar.myCollectionsSection')}</h2>
              <Button
                onClick={openCreateCollectionDialog}
                size="sm"
                variant="outline"
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Collection
              </Button>
            </div>

            {isLoadingCollections ? (
              <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
                <Loader2 className="w-6 h-6 text-[#be43ff] animate-spin" aria-hidden />
                <span className="sr-only">{CONTENT_HUB_LOADING}</span>
              </div>
            ) : collections.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 flex items-center justify-between gap-6">
                <div className="flex items-center gap-4 min-w-0">
                  <button
                    type="button"
                    onClick={openCreateCollectionDialog}
                    className="w-12 h-12 rounded-2xl bg-[#be43ff]/10 border border-[#be43ff]/20 flex items-center justify-center flex-shrink-0 transition-colors hover:bg-[#be43ff]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff] focus-visible:ring-offset-2"
                    aria-label="Create collection"
                  >
                    <FolderPlus className="w-6 h-6 text-[#be43ff]" />
                  </button>
                  <div className="min-w-0">
                    <div className="text-[#101828] text-sm" style={{ fontWeight: 700 }}>
                      No collections yet
                    </div>
                    <div className="text-[#667085] text-sm">
                      Create your first collection to organize and save content.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {collections.map((collection, index) => {
                  const gradient = COLLECTION_CARD_GRADIENTS[index % COLLECTION_CARD_GRADIENTS.length];
                  const isSelected = selectedTreeNodeId === collection.collection_id;
                  const previewUrl =
                    collection.picture_url || collectionPreviewById[collection.collection_id] || null;
                  const itemsCount =
                    typeof collection.content_count === 'number'
                      ? collection.content_count
                      : typeof collection.media_count === 'number'
                        ? collection.media_count
                        : typeof collection.item_count === 'number'
                          ? collection.item_count
                          : null;
                  return (
                    <CollectionCard
                      key={collection.collection_id}
                      collection={collection}
                      gradient={gradient}
                      isSelected={isSelected}
                      previewUrl={previewUrl}
                      itemsCount={itemsCount}
                      onSelect={() => handleCollectionSelect(collection)}
                      onEdit={() => handleCollectionEdit(collection)}
                      onPreviewLoaded={handleCollectionPreviewLoaded}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* All Content Section */}
          <div className="px-6 pb-6">
            <h2 className="text-gray-900 mb-4">
              {activeFilters.showFavoritesOnly ? CONTENT_HUB_SECTION_TITLE_FAVORITES : CONTENT_HUB_SECTION_TITLE_CONTENT}
            </h2>
            {isLoadingContentHubMedias ? (
              <div className="flex items-center justify-center py-16" role="status" aria-live="polite">
                <Loader2 className="w-8 h-8 text-[#be43ff] animate-spin" aria-hidden />
                <span className="sr-only">{CONTENT_HUB_LOADING}</span>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap" style={{ gap: '10px' }}>
                  {filteredContent.map((item) => (
                    <ContentTile
                      key={item.id}
                      item={item}
                      isSelected={selectedItems.includes(item.id)}
                      isFavorite={activeFilters.showFavoritesOnly || favorites.includes(item.mediaId)}
                      isHovered={hoveredContent === item.id}
                      onToggleSelection={toggleItemSelection}
                      onToggleFavorite={toggleFavorite}
                      onOpenPreview={openContentPreview}
                      onMouseEnter={handleTileMouseEnter}
                      onMouseLeave={handleTileMouseLeave}
                    />
                  ))}
                </div>
                {/* Sentinel for IntersectionObserver-driven infinite scroll. */}
                <div ref={sentinelRef} className="h-1 w-full" aria-hidden />
                {isFetchingNextPage && (
                  <div
                    className="flex items-center justify-center py-6"
                    role="status"
                    aria-live="polite"
                  >
                    <Loader2 className="w-6 h-6 text-[#be43ff] animate-spin" aria-hidden />
                    <span className="sr-only">{CONTENT_HUB_LOADING}</span>
                  </div>
                )}
                {!hasNextPage && filteredContent.length > 0 && (
                  <div className="text-center text-[12px] text-[#98a2b3] py-6">
                    {i18n.t('contentHub.pagination.endReached')}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        </div>
      </div>
        </Panel>
      </PanelGroup>

      {/* Content Detail Modal */}
      {selectedContent && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex flex-col backdrop-blur-sm"
          onClick={() => closeContentPreview()}
        >
          {/* Main content area */}
          <div className="flex-1 flex items-center justify-center p-8 min-h-0">
            {/* Prev Arrow */}
            {hasPrev && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateContent('prev'); }}
                className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all mr-4"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            {!hasPrev && <div className="w-10 mr-4 flex-shrink-0" />}

            <div 
              className="bg-white rounded-2xl shadow-2xl overflow-hidden flex"
              style={{ width: '900px', height: '540px' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Left Side - Media */}
              <div className="bg-gray-50 flex items-center justify-center relative flex-shrink-0" style={{ width: '560px' }}>
                {selectedContent.type === 'video' && selectedContentUrl ? (
                  <VideoPlayer
                    ref={previewVideoRef}
                    className="w-full h-full object-contain"
                    src={selectedContentUrl}
                    poster={selectedContent.posterUrl ?? undefined}
                    controls={isPreviewVideoPlaying}
                    preload="auto"
                    onPlay={() => setIsPreviewVideoPlaying(true)}
                  />
                ) : (
                  <img
                    src={selectedContent.posterUrl || selectedContent.thumbnail}
                    alt={selectedContent.title}
                    className="w-full h-full object-contain"
                  />
                )}
                {selectedContent.type === 'video' && !isPreviewVideoPlaying && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                    <button
                      type="button"
                      onClick={() => {
                        setIsPreviewVideoPlaying(true);
                        void previewVideoRef.current?.play();
                      }}
                      className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                      aria-label="Play video"
                      disabled={!selectedContentUrl}
                    >
                      <Play className="w-10 h-10 text-gray-900 ml-1" />
                    </button>
                  </div>
                )}
                {selectedContent.type === 'video' && selectedContent.duration && (
                  <div className="absolute bottom-6 right-6 px-3 py-1.5 bg-black/80 text-white text-sm rounded-lg">
                    {selectedContent.duration}
                  </div>
                )}
                {/* Position indicator */}
                <div className="absolute top-3 left-3 px-2 py-1 bg-black/40 backdrop-blur-sm text-white/80 rounded-md" style={{ fontSize: '11px' }}>
                  {currentContentIndex >= 0
                    ? i18n.t('contentHub.preview.positionOf', { current: currentContentIndex + 1, total: filteredContent.length })
                    : i18n.t('contentHub.preview.positionUnknown')}
                </div>
              </div>

            {/* Right Side - Metadata (minimal) */}
            <div className="flex-1 bg-white flex flex-col min-w-0">
              {/* Header */}
              <div className="px-5 pt-5 pb-4 flex items-start justify-between">
                <div className="flex-1 pr-3 min-w-0">
                  <FilenameTooltip filename={selectedContent.title}>
                    <h2 className="text-gray-900 text-base line-clamp-2 mb-1">{selectedContent.title}</h2>
                  </FilenameTooltip>
                  <p className="text-xs text-gray-400">{selectedContent.creator}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={(e) => toggleFavorite(selectedContent, e)}
                    className={`p-1.5 rounded-full transition-colors ${
                      activeFilters.showFavoritesOnly || favorites.includes(selectedContent.mediaId)
                        ? 'text-[#a9f600]'
                        : 'text-gray-300 hover:text-[#a9f600]'
                    }`}
                  >
                    <Star className={`w-4 h-4 ${(activeFilters.showFavoritesOnly || favorites.includes(selectedContent.mediaId)) ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={() => closeContentPreview()}
                    className="p-1.5 text-gray-300 hover:text-gray-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Details + Rights */}
              <div className="px-5 flex-1 flex flex-col">
                {/* File details */}
                <div className="space-y-0 text-sm">
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-400">Type</span>
                    <span className="text-gray-700">{selectedContent.type === 'video' ? 'Video' : 'Image'}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-400">Campaign</span>
                    <span className="text-gray-700 truncate ml-4">{selectedContent.campaign}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-400">Uploaded</span>
                    <span className="text-gray-700">{selectedContent.uploadDate}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-400">Format</span>
                    <span className="text-gray-700">{selectedContent.format}</span>
                  </div>
                  {selectedContent.duration && (
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-400">Duration</span>
                      <span className="text-gray-700">{selectedContent.duration}</span>
                    </div>
                  )}
                </div>

                <div className="flex-1" />

                {/* Rights section — pinned to bottom */}
                <div className="space-y-0 text-sm">
                  {selectedContent.rights.map((right) => (
                    <div key={right.type} className="relative flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-400 flex items-center gap-1.5">
                        {right.label}
                        <button
                          onClick={() => setActiveTooltip(activeTooltip === right.type ? null : right.type)}
                          onMouseEnter={() => setActiveTooltip(right.type)}
                          onMouseLeave={() => setActiveTooltip(null)}
                          className="text-gray-300 hover:text-gray-500 transition-colors"
                        >
                          <Info className="w-3 h-3" />
                        </button>
                      </span>
                      <span className={getRightStatusColor(right.expiry)}>
                        {getRightStatusText(right.expiry)}
                      </span>
                      {activeTooltip === right.type && (
                        <div className="absolute left-0 bottom-full mb-1.5 z-50 w-64 px-3 py-2 bg-gray-900 text-white rounded-lg shadow-lg" style={{ fontSize: '11px', lineHeight: '1.5' }}>
                          {rightDescriptions[right.type]}
                          <div className="absolute left-4 top-full w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-gray-900" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="px-5 py-4 flex gap-2">
                <Button
                  className="flex-1 min-w-0 shrink bg-purple-600 hover:bg-purple-700 text-white"
                  size="sm"
                  onClick={downloadSelectedContent}
                  disabled={isExpired(selectedContent) || isDownloading}
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-w-0 shrink"
                  onClick={addSelectedContentToCollection}
                  disabled={isLoadingCollections}
                >
                  <FolderPlus className="w-3.5 h-3.5 mr-1.5" />
                  Collect
                </Button>
              </div>
            </div>
          </div>

            {/* Next Arrow */}
            {hasNext && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateContent('next'); }}
                className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all ml-4"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
            {!hasNext && <div className="w-10 ml-4 flex-shrink-0" />}
          </div>

          {/* Filmstrip (only when the open item is part of the filtered grid) */}
          {currentContentIndex >= 0 && filteredContent.length > 0 ? (
          <div 
            className="flex-shrink-0 bg-black/40 backdrop-blur-md px-4 py-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              ref={filmstripRef}
              className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide justify-center"
            >
              {filteredContent.map((item) => {
                const isActive = item.mediaId === selectedContent.mediaId;
                return (
                  <button
                    key={item.id}
                    data-active={isActive}
                    onClick={() => openContentPreview(item)}
                    className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden transition-all ${
                      isActive 
                        ? 'ring-2 ring-[#be43ff] opacity-100 scale-105' 
                        : 'opacity-50 hover:opacity-80'
                    }`}
                  >
                    {item.type === 'video' ? (
                      item.posterUrl ? (
                        <img
                          src={item.posterUrl}
                          alt={item.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-800/40 flex items-center justify-center">
                          <Play className="w-4 h-4 text-white/80 ml-0.5" />
                        </div>
                      )
                    ) : (
                      <img
                        src={item.thumbnail}
                        alt={item.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          ) : null}
        </div>
      )}

      {/* Create Collection Dialog */}
      <Dialog
        open={isCreateCollectionOpen}
        onOpenChange={(open) => {
          setIsCreateCollectionOpen(open);
          if (open) {
            // Always default to Private when opening the Create dialog.
            setNewCollectionVisibility('PRIVATE');
          }
          if (!open) {
            setNewCollectionName('');
            setNewCollectionDescription('');
            setNewCollectionVisibility('PRIVATE');
            setNewPictureFile(null);
            if (resumeAddToCollectionAfterCreate) {
              setIsAddToCollectionOpen(true);
              setResumeAddToCollectionAfterCreate(false);
            }
          }
        }}
      >
        <DialogContent className="max-w-[480px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="text-[#101828] text-[18px]" style={{ fontWeight: 700 }}>
              Create Collection
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                {newPicturePreviewUrl ? (
                  <img
                    src={newPicturePreviewUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Folder className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-[#be43ff] hover:text-[#a830e0] cursor-pointer">
                  <Upload className="w-4 h-4" />
                  Upload Cover Picture
                  <input
                    type="file"
                    accept={ACCEPTED_COLLECTION_PICTURE_TYPES.join(',')}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      if (!f) {
                        setNewPictureFile(null);
                        return;
                      }
                      const err = validateCollectionPictureFile(f);
                      if (err) {
                        showError(err);
                        e.currentTarget.value = '';
                        setNewPictureFile(null);
                        return;
                      }
                      setNewPictureFile(f);
                    }}
                  />
                </label>
              </div>
            </div>
            <div>
              <label className="text-[#344054] text-xs mb-1.5 block">Collection name *</label>
              <Input
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                placeholder="Enter collection name"
              />
            </div>
            <div>
              <label className="text-[#344054] text-xs mb-1.5 block">Description</label>
              <Textarea
                value={newCollectionDescription}
                onChange={(e) => setNewCollectionDescription(e.target.value)}
                placeholder="Optional description"
                className="min-h-[96px]"
              />
            </div>
            <div>
              <label className="text-[#344054] text-xs mb-1.5 block">{CONTENT_HUB_COLLECTION_VISIBILITY_LABEL}</label>
              <select
                className="border-input bg-input-background flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm"
                value={newCollectionVisibility}
                onChange={(e) => setNewCollectionVisibility(normalizeCollectionVisibility(e.target.value))}
              >
                <option value="PRIVATE">{CONTENT_HUB_COLLECTION_VISIBILITY_PRIVATE}</option>
                <option value="TEAM">{CONTENT_HUB_COLLECTION_VISIBILITY_TEAM}</option>
              </select>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => setIsCreateCollectionOpen(false)}
                disabled={isSavingCollection}
              >
                Cancel
              </Button>
              <Button
                className="bg-[#2e024c] hover:bg-[#3a0d5e] text-white"
                disabled={isSavingCollection || newCollectionName.trim().length === 0}
                onClick={async () => {
                  const token = getTokenFromStorage();
                  if (!token) return;
                  const name = newCollectionName.trim();
                  if (!name) return;
                  if (isSavingCollection) return;

                  setIsSavingCollection(true);
                  try {
                    const created = await createContentHubCollection(token, {
                      name,
                      description: newCollectionDescription.trim() ? newCollectionDescription.trim() : null,
                      visibility: newCollectionVisibility,
                    });
                    if (created?.collection_id && newPictureFile) {
                      const fileToUpload = await prepareImageForUpload(newPictureFile, {
                        maxDimension: 1200,
                        // Keep payload under common reverse proxy limits (often ~1MB).
                        maxBytes: 900 * 1024,
                      });
                      if (fileToUpload.size > 900 * 1024) {
                        showError(`Image is still too large after compression (${Math.round(fileToUpload.size / 1024)}KB). Please try a smaller image.`);
                        throw new Error('Image too large after compression');
                      }
                      await uploadContentHubCollectionPicture(token, created.collection_id, fileToUpload);
                    }
                    setIsCreateCollectionOpen(false);
                    setNewCollectionName('');
                    setNewCollectionDescription('');
                    setNewPictureFile(null);
                    await refreshCollections();
                    if (created?.collection_id) {
                      setPendingSelectAddToCollectionId(created.collection_id);
                    }
                  } catch {
                    // Keep UI as-is on error.
                  } finally {
                    setIsSavingCollection(false);
                  }
                }}
              >
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add to Collection Dialog */}
      <Dialog
        open={isAddToCollectionOpen}
        onOpenChange={(open) => {
          setIsAddToCollectionOpen(open);
          if (!open) {
            if (preserveAddToCollectionSelectionOnClose) {
              setPreserveAddToCollectionSelectionOnClose(false);
            } else {
              setSelectedAddToCollectionIds([]);
            }
          }
        }}
      >
        <DialogContent className="max-w-[480px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="text-[#101828] text-[18px]" style={{ fontWeight: 700 }}>
              Add to Collection{collectionActionMediaIds.length > 0 ? ` (${collectionActionMediaIds.length} item${collectionActionMediaIds.length === 1 ? '' : 's'})` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-3">
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              <button
                type="button"
                onClick={() => {
                  setPreserveAddToCollectionSelectionOnClose(true);
                  setResumeAddToCollectionAfterCreate(true);
                  setIsAddToCollectionOpen(false);
                  openCreateCollectionDialog();
                }}
                className="w-full flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-left transition-colors hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-[#be43ff]/10 border border-[#be43ff]/20 flex items-center justify-center flex-shrink-0">
                    <Plus className="w-5 h-5 text-[#be43ff]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-[#101828] truncate" style={{ fontWeight: 600 }}>
                      New Collection
                    </div>
                    <div className="text-xs text-[#667085] line-clamp-1">Create a new collection</div>
                  </div>
                </div>
                <div className="w-5 h-5 rounded-md border border-gray-300 bg-white flex-shrink-0" />
              </button>
              {collections.map((c) => {
                const isActive = selectedAddToCollectionIds.includes(c.collection_id);
                return (
                  <button
                    key={c.collection_id}
                    type="button"
                    onClick={() => {
                      setSelectedAddToCollectionIds((prev) => {
                        if (prev.includes(c.collection_id)) return prev.filter((id) => id !== c.collection_id);
                        return [...prev, c.collection_id];
                      });
                    }}
                    className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${
                      isActive ? 'border-[#be43ff] bg-[#be43ff]/5' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-[#101828] truncate" style={{ fontWeight: 600 }}>
                        {c.name}
                      </div>
                      {c.description ? (
                        <div className="text-xs text-[#667085] line-clamp-1">{c.description}</div>
                      ) : null}
                    </div>
                    <div className={`w-5 h-5 rounded-md border flex-shrink-0 inline-flex items-center justify-center ${
                      isActive ? 'border-[#be43ff] bg-[#be43ff]' : 'border-gray-300 bg-white'
                    }`}>
                      {isActive ? <Check className="w-3.5 h-3.5 text-white" /> : null}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsAddToCollectionOpen(false)} disabled={isSavingCollection}>
                {collections.length === 0 ? 'Close' : 'Cancel'}
              </Button>
              {collections.length > 0 ? (
                <Button
                  className="bg-[#2e024c] hover:bg-[#3a0d5e] text-white"
                  disabled={isSavingCollection || selectedAddToCollectionIds.length === 0}
                  onClick={confirmAddToCollection}
                >
                  Add
                </Button>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Collection Dialog */}
      <Dialog
        open={isEditCollectionOpen}
        onOpenChange={(open) => {
          setIsEditCollectionOpen(open);
          if (!open) {
            setEditingCollectionId(null);
            setEditCollectionName('');
            setEditCollectionDescription('');
            setEditCollectionVisibility('PRIVATE');
            setEditCollectionOriginalVisibility('PRIVATE');
            setEditPictureFile(null);
          }
        }}
      >
        <DialogContent className="max-w-[520px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="text-[#101828] text-[18px]" style={{ fontWeight: 700 }}>
              Collection
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                {(() => {
                  const existingUrl = collections.find((c) => c.collection_id === editingCollectionId)?.picture_url ?? null;
                  const previewUrl = editPicturePreviewUrl ?? existingUrl;
                  if (!previewUrl || isVideoUrl(previewUrl)) return <Folder className="w-5 h-5 text-gray-400" />;
                  return (
                    <img
                      src={previewUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-[#be43ff] hover:text-[#a830e0] cursor-pointer">
                  <Upload className="w-4 h-4" />
                  Upload Cover Picture
                  <input
                    type="file"
                    accept={ACCEPTED_COLLECTION_PICTURE_TYPES.join(',')}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      if (!f) {
                        setEditPictureFile(null);
                        return;
                      }
                      const err = validateCollectionPictureFile(f);
                      if (err) {
                        showError(err);
                        e.currentTarget.value = '';
                        setEditPictureFile(null);
                        return;
                      }
                      setEditPictureFile(f);
                    }}
                  />
                </label>
              </div>
            </div>
            <div>
              <label className="text-[#344054] text-xs mb-1.5 block">Collection name *</label>
              <Input value={editCollectionName} onChange={(e) => setEditCollectionName(e.target.value)} />
            </div>
            <div>
              <label className="text-[#344054] text-xs mb-1.5 block">Description</label>
              <Textarea
                value={editCollectionDescription}
                onChange={(e) => setEditCollectionDescription(e.target.value)}
                className="min-h-[96px]"
              />
            </div>
            <div>
              <label className="text-[#344054] text-xs mb-1.5 block">{CONTENT_HUB_COLLECTION_VISIBILITY_LABEL}</label>
              <select
                className="border-input bg-input-background flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm"
                value={editCollectionVisibility}
                onChange={(e) => setEditCollectionVisibility(normalizeCollectionVisibility(e.target.value))}
              >
                <option value="PRIVATE">{CONTENT_HUB_COLLECTION_VISIBILITY_PRIVATE}</option>
                <option value="TEAM">{CONTENT_HUB_COLLECTION_VISIBILITY_TEAM}</option>
              </select>
            </div>
            <div className="flex items-center justify-between pt-1">
              <Button
                variant="outline"
                className="text-[#ff5635] border-[#ff5635]/30 hover:bg-[#ff5635]/10"
                disabled={isSavingCollection || isArchivingCollection || !editingCollectionId}
                onClick={async () => {
                  const token = getTokenFromStorage();
                  if (!token) return;
                  if (!editingCollectionId) return;
                  if (isArchivingCollection) return;
                  setIsArchivingCollection(true);
                  try {
                    await archiveContentHubCollection(token, editingCollectionId);
                    setIsEditCollectionOpen(false);
                    await refreshCollections();
                  } catch {
                    // Keep UI as-is on error.
                  } finally {
                    setIsArchivingCollection(false);
                  }
                }}
              >
                Archive
              </Button>

              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setIsEditCollectionOpen(false)} disabled={isSavingCollection || isArchivingCollection}>
                  Cancel
                </Button>
                <Button
                  className="bg-[#2e024c] hover:bg-[#3a0d5e] text-white"
                  disabled={isSavingCollection || isArchivingCollection || !editingCollectionId || editCollectionName.trim().length === 0}
                  onClick={async () => {
                    const token = getTokenFromStorage();
                    if (!token) return;
                    if (!editingCollectionId) return;
                    const name = editCollectionName.trim();
                    if (!name) return;
                    if (isSavingCollection) return;

                    setIsSavingCollection(true);
                    try {
                      const wantsVisibilityChange = editCollectionVisibility !== editCollectionOriginalVisibility;
                      if (!wantsVisibilityChange) {
                        await updateContentHubCollection(token, editingCollectionId, {
                          name,
                          description: editCollectionDescription.trim() ? editCollectionDescription.trim() : null,
                        });
                        if (editPictureFile) {
                          const fileToUpload = await prepareImageForUpload(editPictureFile, {
                            maxDimension: 1200,
                            maxBytes: 900 * 1024,
                          });
                          if (fileToUpload.size > 900 * 1024) {
                            showError(`Image is still too large after compression (${Math.round(fileToUpload.size / 1024)}KB). Please try a smaller image.`);
                            throw new Error('Image too large after compression');
                          }
                          await uploadContentHubCollectionPicture(token, editingCollectionId, fileToUpload);
                        }
                      } else {
                        // API does not support updating visibility on PATCH collection/update.
                        // Workaround: create a new collection with the desired visibility, copy medias, then archive the old one.
                        const created = await createContentHubCollection(token, {
                          name,
                          description: editCollectionDescription.trim() ? editCollectionDescription.trim() : null,
                          visibility: editCollectionVisibility,
                        });
                        if (!created?.collection_id) {
                          throw new Error('Failed to create collection');
                        }

                        if (editPictureFile) {
                          const fileToUpload = await prepareImageForUpload(editPictureFile, {
                            maxDimension: 1200,
                            maxBytes: 900 * 1024,
                          });
                          if (fileToUpload.size > 900 * 1024) {
                            showError(`Image is still too large after compression (${Math.round(fileToUpload.size / 1024)}KB). Please try a smaller image.`);
                            throw new Error('Image too large after compression');
                          }
                          await uploadContentHubCollectionPicture(token, created.collection_id, fileToUpload);
                        }

                        const allMediaIds: string[] = [];
                        const pageSize = 200;
                        let pageNumber = 0;
                        for (let i = 0; i < 30; i += 1) {
                          const page = await getContentHubSearch(token, {
                            collectionId: editingCollectionId,
                            pageNumber,
                            pageSize,
                            text: null,
                          });
                          const medias = Array.isArray(page?.medias) ? page.medias : [];
                          medias.forEach((m) => {
                            const id = typeof m.media_id === 'string' ? m.media_id : '';
                            if (id) allMediaIds.push(id);
                          });
                          if (!page?.has_more) break;
                          pageNumber += 1;
                        }

                        const chunkSize = 50;
                        for (let i = 0; i < allMediaIds.length; i += chunkSize) {
                          const chunk = allMediaIds.slice(i, i + chunkSize);
                          if (chunk.length === 0) continue;
                          await addContentHubCollectionMedias(token, created.collection_id, chunk);
                        }

                        await archiveContentHubCollection(token, editingCollectionId);

                        if (selectedTreeNodeId === editingCollectionId) {
                          setSelectedTreeNodeId(created.collection_id);
                          setSelectedFolderId(created.collection_id);
                        }
                      }

                      setIsEditCollectionOpen(false);
                      await refreshCollections();
                    } catch {
                      // Keep UI as-is on error.
                    } finally {
                      setIsSavingCollection(false);
                    }
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}